import { FloatingWindowController } from "../workspace/floating-window-controller.js";
import { indicatorPaneValueAtY } from "../overlays/chart-overlay.js";
import { calculateIndicator } from "../indicators/indicator-calculations.js";
import { getIndicatorDefinition } from "../indicators/indicator-definitions.js";
import {
  enhanceChartDropdowns,
  refreshChartDropdown,
  syncChartDropdown
} from "./chart-dropdown.js";
import { AlertExpirationPicker } from "./alert-expiration-picker.js";
import { LocalAlertCoordinator } from "./local-alert-coordinator.js";
import { LocalAlertRepository } from "./local-alert-repository.js";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { SUPPORTED_CHART_TIMEFRAMES } from "../core/chart-timeframes.js";

const MANAGER_TABS = ["Active", "Log"];
const OPERATORS = [
  ["CrossesAbove", "Crosses above"],
  ["CrossesBelow", "Crosses below"],
  ["CrossesEither", "Crosses either direction"]
];

function rememberBounded(set, value, maximum = 500) {
  set.add(value);
  if (set.size <= maximum) return;
  set.delete(set.values().next().value);
}

function rememberTriggerForToast(set, triggerId) {
  if (!triggerId || set.has(triggerId)) return false;
  rememberBounded(set, triggerId);
  return true;
}

export class AlertRepository {
  constructor({
    productApiEndpoint = null,
    alertsEndpoint = null,
    historyEndpoint = null,
    unreadEndpoint = null,
    chartResourcesEndpoint = null
  } = {}) {
    this.alertsEndpoint = normalizeEndpoint(alertsEndpoint);
    this.historyEndpoint = normalizeEndpoint(historyEndpoint);
    this.unreadEndpoint = normalizeEndpoint(unreadEndpoint);
    this.readAllEndpoint = null;
    this.indicatorCapabilitiesEndpoint = null;
    this.indicatorPreviewEndpoint = null;
    this.indicatorPairPreviewEndpoint = null;
    this.chartResourcesEndpoint = chartResourcesEndpoint;
    this.capabilityPromise = null;
    if (productApiEndpoint) this.setProductApiEndpoint(productApiEndpoint);
  }

  async listAll() {
    const endpoint = this.requiredProductEndpoint(this.alertsEndpoint, "alerts");
    const alerts = [];
    let page = 1;
    while (page <= 10) {
      const result = await this.request(
        `${endpoint}?page=${page}&pageSize=100&sort=updated&descending=true`
      );
      alerts.push(...(result.items || []));
      if (alerts.length >= Number(result.total || 0) || (result.items || []).length === 0) {
        break;
      }
      page += 1;
    }
    return alerts;
  }

  async history({ isRead = null } = {}) {
    const query = new URLSearchParams({ page: "1", pageSize: "100" });
    if (isRead !== null) query.set("isRead", String(isRead));
    const endpoint = this.requiredProductEndpoint(this.historyEndpoint, "alert history");
    const result = await this.request(`${endpoint}?${query}`);
    return result.items || [];
  }

  create(payload) {
    return this.request(this.requiredProductEndpoint(this.alertsEndpoint, "alerts"), {
      method: "POST",
      body: JSON.stringify(payload)
    });
  }

  update(alert, payload) {
    const endpoint = this.requiredProductEndpoint(this.alertsEndpoint, "alerts");
    return this.request(`${endpoint}/${alert.id}`, {
      method: "PUT",
      body: JSON.stringify({ ...payload, expectedVersion: alert.version })
    });
  }

  action(alert, action) {
    const endpoint = this.requiredProductEndpoint(this.alertsEndpoint, "alerts");
    const options = { method: "POST" };
    if (action !== "duplicate") {
      options.body = JSON.stringify({ expectedVersion: alert.version });
    }
    return this.request(`${endpoint}/${alert.id}/${action}`, options);
  }

  remove(alert) {
    const endpoint = this.requiredProductEndpoint(this.alertsEndpoint, "alerts");
    return this.request(
      `${endpoint}/${alert.id}?expectedVersion=${alert.version}`,
      { method: "DELETE" }
    );
  }

  bulk(action, ids) {
    const endpoint = this.requiredProductEndpoint(this.alertsEndpoint, "alerts");
    return this.request(`${endpoint}/bulk/${action}`, {
      method: "POST",
      body: JSON.stringify({ alertIds: ids })
    });
  }

  unreadCount() {
    return this.request(this.requiredProductEndpoint(this.unreadEndpoint, "notifications"));
  }

  markRead(triggerId) {
    const endpoint = this.requiredProductEndpoint(this.historyEndpoint, "alert history");
    return this.request(`${endpoint}/${triggerId}/read`, { method: "POST" });
  }

  markAllRead() {
    return this.request(
      this.requiredProductEndpoint(this.readAllEndpoint, "notifications"),
      { method: "POST" }
    );
  }

  capabilities() {
    this.capabilityPromise ||= this.request(this.requiredProductEndpoint(
      this.indicatorCapabilitiesEndpoint,
      "indicator alert capabilities"
    ));
    return this.capabilityPromise;
  }

  previewIndicator(payload) {
    return this.request(this.requiredProductEndpoint(
      this.indicatorPreviewEndpoint,
      "indicator alert preview"
    ), {
      method: "POST",
      body: JSON.stringify(payload)
    });
  }

  previewIndicatorPair(payload) {
    return this.request(this.requiredProductEndpoint(
      this.indicatorPairPreviewEndpoint,
      "indicator alert preview"
    ), {
      method: "POST",
      body: JSON.stringify(payload)
    });
  }

  upsertIndicator(chartId, indicatorId, indicator, expectedVersion = null) {
    return this.request(
      `${this.requiredChartResourcesEndpoint()}/${encodeURIComponent(chartId)}/indicators/${encodeURIComponent(indicatorId)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          indicatorId: indicator.indicatorId,
          algorithmVersion: indicator.algorithmVersion,
          parameters: indicator.parameters,
          inputSeries: indicator.inputSeries,
          outputSeries: indicator.outputSeries,
          expectedVersion
        })
      }
    );
  }

  deleteIndicator(chartId, indicatorId, expectedVersion) {
    return this.request(
      `${this.requiredChartResourcesEndpoint()}/${encodeURIComponent(chartId)}/indicators/${encodeURIComponent(indicatorId)}?expectedVersion=${expectedVersion}`,
      { method: "DELETE" }
    );
  }

  upsertDrawing(chartId, drawingId, price, expectedVersion = null) {
    return this.request(
      `${this.requiredChartResourcesEndpoint()}/${encodeURIComponent(chartId)}/drawings/${encodeURIComponent(drawingId)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          type: "horizontal-line",
          price,
          expectedVersion
        })
      }
    );
  }

  deleteDrawing(chartId, drawingId, expectedVersion) {
    return this.request(
      `${this.requiredChartResourcesEndpoint()}/${encodeURIComponent(chartId)}/drawings/${encodeURIComponent(drawingId)}?expectedVersion=${expectedVersion}`,
      { method: "DELETE" }
    );
  }

  setChartResourcesEndpoint(endpoint) {
    this.chartResourcesEndpoint = typeof endpoint === "string"
      ? endpoint.replace(/\/$/, "")
      : null;
  }

  setProductApiEndpoint(endpoint) {
    const productApi = normalizeEndpoint(endpoint);
    if (!productApi) {
      this.alertsEndpoint = null;
      this.historyEndpoint = null;
      this.unreadEndpoint = null;
      this.readAllEndpoint = null;
      this.indicatorCapabilitiesEndpoint = null;
      this.indicatorPreviewEndpoint = null;
      this.indicatorPairPreviewEndpoint = null;
      this.capabilityPromise = null;
      return;
    }
    this.alertsEndpoint = `${productApi}/alerts`;
    this.historyEndpoint = `${productApi}/alert-triggers`;
    this.unreadEndpoint = `${productApi}/notifications/unread-count`;
    this.readAllEndpoint = `${productApi}/notifications/read-all`;
    this.indicatorCapabilitiesEndpoint = `${productApi}/indicator-capabilities`;
    this.indicatorPreviewEndpoint = `${productApi}/indicator-alerts/preview`;
    this.indicatorPairPreviewEndpoint = `${productApi}/indicator-alerts/pair-preview`;
    this.capabilityPromise = null;
  }

  requiredProductEndpoint(endpoint, resource) {
    if (!endpoint) {
      throw new Error(`Product-qualified ${resource} are unavailable for this chart launch.`);
    }
    return endpoint;
  }

  requiredChartResourcesEndpoint() {
    if (!this.chartResourcesEndpoint) {
      throw new Error("Product-qualified chart resources are unavailable for this chart launch.");
    }
    return this.chartResourcesEndpoint;
  }

  async request(url, options = {}) {
    const method = String(options.method || "GET").toUpperCase();
    const headers = {
      "Accept": "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers
    };
    if (!["GET", "HEAD"].includes(method)) {
      const token = globalThis.document
        ?.querySelector?.('meta[name="csrf-token"]')
        ?.content;
      if (token) headers["X-CSRF-TOKEN"] = token;
    }
    const response = await fetch(url, {
      ...options,
      method,
      credentials: "same-origin",
      headers
    });
    const type = response.headers.get("content-type") || "";
    const payload = response.status === 204
      ? null
      : type.includes("json")
        ? await response.json()
        : await response.text();
    if (!response.ok) {
      const error = new Error(
        alertPolicyMessage(
          payload?.code,
          payload?.detail || payload?.title || payload || "The alert request failed."
        )
      );
      error.status = response.status;
      error.code = payload?.code || null;
      error.validationErrors = payload?.errors || null;
      throw error;
    }
    return payload;
  }
}

export class AlertWorkspace {
  constructor({
    root,
    surface,
    openButton,
    getActiveChart,
    getCharts,
    focusChart,
    onFocusWindow = () => {},
    onWorkspaceChange = () => {},
    onWindowStateChange = () => {},
    nextStackOrder = null,
    announce = () => {}
  }) {
    this.root = root;
    this.surface = surface;
    this.openButton = openButton;
    this.getActiveChart = getActiveChart;
    this.getCharts = getCharts;
    this.focusChart = focusChart;
    this.onFocusWindow = onFocusWindow;
    this.onWorkspaceChange = onWorkspaceChange;
    this.onWindowStateChange = onWindowStateChange;
    this.nextStackOrder = nextStackOrder;
    this.announce = announce;
    this.localMode = root.dataset.alertEvaluationMode !== "server";
    this.repository = null;
    this.storageScope = null;
    this.alertHubEndpoint = null;
    this.canCreateAlerts = true;
    this.maximumActiveAlerts = Number.POSITIVE_INFINITY;
    this.localQuotaSuspendedIds = new Set();
    this.alerts = [];
    this.triggers = [];
    this.managerTab = "Active";
    this.managerSearch = "";
    this.managerSort = "updated";
    this.managerSortOpen = false;
    this.managerHasOpened = false;
    this.managerStackOrder = 0;
    this.editorHasOpened = false;
    this.selectedIds = new Set();
    this.unreadInitialized = false;
    this.lastUnreadCount = 0;
    this.drag = null;
    this.pollTimer = null;
    this.realtimeRetryTimer = null;
    this.realtimeRetryAttempt = 0;
    this.realtimeStartPending = false;
    this.hasConnectedRealtime = false;
    this.disposed = false;
    this.hub = null;
    this.hubConnected = false;
    this.seenRealtimeEventIds = new Set();
    this.seenTriggerIds = new Set();
    this.drawingVersions = new Map();
    this.indicatorVersions = new Map();
    this.editorIndicatorChoices = [];
    this.editorIndicatorChoicesByKey = new Map();
    this.editorIndicatorB = null;
    this.editorPreviewTimer = null;
    this.editorPreviewGeneration = 0;
    this.refreshController = null;
    this.focusListeners = new AbortController();
    this.objectTreeListeners = new Set();
    this.createManagerWindow();
    this.createEditorWindow();
    this.createAlertContextMenu();
    this.createDrawingDeleteDialog();
    this.createToast();
    this.bind();
  }

  setChartResourcesEndpoint(endpoint) {
    if (this.repository instanceof AlertRepository) {
      this.repository.setChartResourcesEndpoint(endpoint);
    }
  }

  configureProductContext({
    productApiEndpoint,
    alertHubEndpoint,
    chartResourcesEndpoint,
    storageScope
  }) {
    if (!storageScope) {
      throw new Error("A product-qualified alert storage scope is required.");
    }
    this.storageScope = storageScope;
    this.alertHubEndpoint = alertHubEndpoint;
    this.repository = this.localMode
      ? new LocalAlertRepository({
          storageScope,
          maximumActiveAlerts: this.maximumActiveAlerts,
          resolveSymbol: (symbolId) =>
            this.getCharts().find((chart) =>
              sameIdentifier(chart.symbolId, symbolId))?.symbol || symbolId,
          onMutation: (kind, payload) =>
            this.handleLocalRepositoryMutation(kind, payload)
        })
      : new AlertRepository({
          productApiEndpoint,
          chartResourcesEndpoint
        });
  }

  setProductPolicy({ canCreateAlerts = true, maximumActiveAlerts = Number.POSITIVE_INFINITY }) {
    this.canCreateAlerts = canCreateAlerts !== false;
    this.maximumActiveAlerts = Number.isFinite(maximumActiveAlerts)
      ? Math.max(0, Number(maximumActiveAlerts))
      : Number.POSITIVE_INFINITY;
    if (this.repository instanceof LocalAlertRepository) {
      this.repository.maximumActiveAlerts = this.maximumActiveAlerts;
    }
    this.syncPolicyControls();
  }

  async initialize() {
    if (!this.repository) {
      throw new Error("Product-qualified alerts are unavailable for this chart launch.");
    }
    if (this.localMode) this.startLocalEvaluation();
    await this.refresh({ quiet: true });
    await this.refreshUnreadCount({ notify: false });
    if (this.localMode) {
      this.bootstrapLocalCharts();
    } else {
      await this.connectRealtime();
    }
  }

  dispose() {
    this.disposed = true;
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    if (this.realtimeRetryTimer) window.clearTimeout(this.realtimeRetryTimer);
    if (this.editorPreviewTimer) window.clearTimeout(this.editorPreviewTimer);
    this.hub?.stop().catch(() => {});
    this.localCoordinator?.dispose();
    this.localWorker?.terminate();
    this.root.removeEventListener(
      "flexcharting:market-observation",
      this.handleLocalObservationEvent
    );
    this.refreshController?.abort();
    this.focusListeners.abort();
    this.managerFloating?.dispose();
    this.editorFloating?.dispose();
    this.expirationPicker?.dispose();
    for (const dropdown of this.editorDropdowns || []) dropdown.destroy();
    this.managerElement.remove();
    this.editorElement.remove();
    this.contextMenu.remove();
    this.deleteDialog.remove();
    this.toast.remove();
    this.objectTreeListeners.clear();
  }

  startLocalEvaluation() {
    this.localWorker = new Worker(
      new URL("../workers/local-alert-worker.js", import.meta.url),
      { type: "module", name: "flexcharting-local-alerts" }
    );
    this.localWorker.addEventListener(
      "message",
      (event) => this.handleLocalWorkerResult(event.data)
    );
    this.localCoordinator = new LocalAlertCoordinator({
      storageScope: this.storageScope,
      onMessage: (message) => this.handleLocalCoordinatorMessage(message),
      onLeadershipChanged: (leader) => {
        this.setRealtimeState(leader ? "local-active" : "local-standby");
        if (leader) this.bootstrapLocalCharts();
      }
    });
    this.handleLocalObservationEvent = (event) => {
      const observation = this.buildLocalObservation(event.detail || {});
      if (observation) this.localCoordinator.submitObservation(observation);
    };
    this.root.addEventListener(
      "flexcharting:market-observation",
      this.handleLocalObservationEvent
    );
    this.localCoordinator.start();
    this.syncLocalWorkerAlerts();
  }

  handleLocalRepositoryMutation(kind, payload) {
    if (!this.localCoordinator) return;
    this.localCoordinator.publish({
      type: kind === "triggered" ? "triggered" : "storage-changed",
      triggerId: payload?.id || null
    });
  }

  async handleLocalCoordinatorMessage(message) {
    if (message.type === "observation" && this.localCoordinator?.isLeader) {
      this.localWorker?.postMessage({
        type: "observe",
        observation: message.observation
      });
      return;
    }
    if (["storage-changed", "triggered"].includes(message.type)) {
      await this.refresh({ quiet: true });
      await this.refreshUnreadCount({ notify: false });
    }
  }

  async handleLocalWorkerResult(result) {
    if (result?.type === "expired") {
      await this.repository.expire(result.alertId);
      await this.refresh({ quiet: true });
      return;
    }
    if (result?.type !== "trigger") return;
    const recorded = await this.repository.recordTrigger(result);
    if (!recorded.created || !recorded.trigger) return;
    rememberTriggerForToast(this.seenTriggerIds, recorded.trigger.id);
    await this.refresh({ quiet: true });
    await this.refreshUnreadCount({ notify: false });
    this.showTriggerToast(recorded.trigger);
    this.showBrowserNotification(recorded.trigger);
  }

  syncLocalWorkerAlerts() {
    const eligible = quotaEligibleAlertIds(this.alerts, this.maximumActiveAlerts);
    this.localQuotaSuspendedIds = new Set(
      this.alerts
        .filter((alert) => alert.status === "Active" && !eligible.has(alert.id))
        .map((alert) => alert.id)
    );
    this.root.dataset.quotaSuspendedAlertCount = String(this.localQuotaSuspendedIds.size);
    this.localWorker?.postMessage({
      type: "set-alerts",
      alerts: this.alerts.filter((alert) =>
        alert.status !== "Active" || eligible.has(alert.id))
    });
  }

  bootstrapLocalCharts() {
    if (!this.localMode || !this.localCoordinator?.isLeader) return;
    window.setTimeout(() => {
      for (const chart of this.getCharts()) {
        const observation = this.buildLocalObservation({
          symbolId: chart.symbolId,
          timeframe: chart.timeframe,
          isFinal: true,
          isBootstrap: true
        });
        if (observation) this.localCoordinator.submitObservation(observation);
      }
    }, 0);
  }

  buildLocalObservation(detail = {}) {
    const symbolId = detail.symbolId || this.getActiveChart()?.symbolId;
    if (!symbolId) return null;
    const instance = this.getCharts().find((chart) =>
      sameIdentifier(chart.symbolId, symbolId)) || null;
    const lastIndex = Math.max(0, Number(instance?.data?.length || 0) - 1);
    const chartTimestamp = Number(instance?.data?.timestamps?.[lastIndex]);
    const timestamp = finiteTimestamp(detail.observedAtUtc)
      ?? (Number.isFinite(chartTimestamp) ? chartTimestamp : Date.now());
    const observedAtUtc = new Date(timestamp).toISOString();
    const price = Number(detail.price ?? lastPrice(instance));
    if (!Number.isFinite(price)) return null;
    const eventId = String(
      detail.eventId
      || `${detail.isBootstrap ? "bootstrap" : "local"}:${symbolId}:${timestamp}:${price}`
    );
    const calculationCache = new Map();
    const valuesByAlertId = { ...(detail.valuesByAlertId || {}) };
    for (const alert of this.alerts.filter((candidate) =>
      sameIdentifier(candidate.symbolId, symbolId))) {
      if (valuesByAlertId[alert.id]) continue;
      valuesByAlertId[alert.id] = {
        indicatorValue: this.resolveLocalIndicatorValue(
          instance,
          alert,
          "",
          detail.indicatorValues,
          calculationCache
        ),
        indicatorBValue: this.resolveLocalIndicatorValue(
          instance,
          alert,
          "B",
          detail.indicatorValues,
          calculationCache
        )
      };
    }
    const watermark = detail.watermark || eventId;
    return {
      eventId,
      symbolId,
      price,
      observedAtUtc,
      sequence: detail.sequence ?? timestamp,
      timeframe: detail.timeframe || instance?.timeframe || null,
      barId: String(
        detail.barId
        || detail.barStartUtc
        || (Number.isFinite(chartTimestamp) ? chartTimestamp : timestamp)
      ),
      barStartUtc: detail.barStartUtc || null,
      isFinal: detail.isFinal === true,
      isBootstrap: detail.isBootstrap === true,
      isRegularSession: detail.isRegularSession,
      watermark,
      indicatorWatermark: detail.indicatorWatermark || watermark,
      valuesByAlertId
    };
  }

  resolveLocalIndicatorValue(instance, alert, suffix, supplied, cache) {
    const indicatorId = alert[`indicator${suffix}Id`];
    if (!indicatorId) return null;
    const outputSeries = alert[`indicator${suffix}OutputSeries`];
    const sourceId = alert[`sourceIndicator${suffix}Id`];
    const suppliedValue = suppliedIndicatorValue(
      supplied,
      alert.id,
      suffix,
      sourceId,
      indicatorId,
      outputSeries
    );
    if (Number.isFinite(suppliedValue)) return suppliedValue;

    if (!instance?.data?.length) return null;

    const parameters = parseParameters(alert[`indicator${suffix}ParametersJson`]);
    const cacheKey = JSON.stringify([indicatorId, parameters, outputSeries]);
    if (!cache.has(cacheKey)) {
      const definition = getIndicatorDefinition(indicatorId);
      if (!definition) return null;
      try {
        const outputs = calculateIndicator(definition, { inputs: parameters }, instance.data);
        cache.set(cacheKey, lastSeriesValue(outputs[outputSeries]));
      } catch {
        cache.set(cacheKey, null);
      }
    }
    return cache.get(cacheKey);
  }

  async connectRealtime() {
    const endpoint = this.alertHubEndpoint;
    if (!endpoint) {
      throw new Error("Product-qualified alert notifications are unavailable.");
    }
    this.hub = new HubConnectionBuilder()
      .withUrl(endpoint)
      .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();
    this.hub.on("AlertEvent", (event) => this.handleRealtimeEvent(event));
    this.hub.onreconnecting(() => {
      this.hubConnected = false;
      this.setRealtimeState("reconnecting");
      this.startFallbackReconciliation();
    });
    this.hub.onreconnected(async () => {
      this.hubConnected = true;
      this.hasConnectedRealtime = true;
      this.realtimeRetryAttempt = 0;
      this.setRealtimeState("connected");
      this.stopFallbackReconciliation();
      await this.reconcileRealtimeState({ notify: true });
    });
    this.hub.onclose(() => {
      this.hubConnected = false;
      this.setRealtimeState("disconnected");
      this.startFallbackReconciliation();
      this.scheduleRealtimeRetry();
    });
    await this.startRealtimeConnection();
  }

  async startRealtimeConnection() {
    if (
      this.disposed ||
      this.hubConnected ||
      this.realtimeStartPending ||
      !this.hub
    ) {
      return;
    }
    this.realtimeStartPending = true;
    try {
      await this.hub.start();
      this.hubConnected = true;
      const notify = this.hasConnectedRealtime;
      this.hasConnectedRealtime = true;
      this.realtimeRetryAttempt = 0;
      this.setRealtimeState("connected");
      this.stopFallbackReconciliation();
      await this.reconcileRealtimeState({ notify });
    } catch {
      this.hubConnected = false;
      this.setRealtimeState("fallback");
      this.startFallbackReconciliation();
      this.scheduleRealtimeRetry();
    } finally {
      this.realtimeStartPending = false;
    }
  }

  scheduleRealtimeRetry() {
    if (
      this.disposed ||
      this.hubConnected ||
      this.realtimeRetryTimer
    ) {
      return;
    }
    const delays = [2_000, 5_000, 10_000, 30_000];
    const delay = delays[Math.min(this.realtimeRetryAttempt, delays.length - 1)];
    this.realtimeRetryAttempt += 1;
    this.realtimeRetryTimer = window.setTimeout(async () => {
      this.realtimeRetryTimer = null;
      await this.startRealtimeConnection();
    }, delay);
  }

  setRealtimeState(state) {
    this.root.dataset.alertRealtimeState = state;
  }

  startFallbackReconciliation() {
    if (this.pollTimer) return;
    this.pollTimer = window.setInterval(() => {
      if (this.hubConnected) return;
      this.reconcileRealtimeState({ notify: true });
    }, 8_000);
  }

  stopFallbackReconciliation() {
    if (!this.pollTimer) return;
    window.clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async reconcileRealtimeState({ notify = false } = {}) {
    await this.refresh({ quiet: true });
    await this.refreshUnreadCount({ notify });
  }

  async handleRealtimeEvent(event) {
    if (!event?.eventId || this.seenRealtimeEventIds.has(event.eventId)) return;
    rememberBounded(this.seenRealtimeEventIds, event.eventId);
    const showTrigger = rememberTriggerForToast(
      this.seenTriggerIds,
      event.triggerId
    );
    await this.reconcileRealtimeState();
    if (event.type === "AlertTriggered" && showTrigger) {
      const trigger = this.triggers.find((item) => item.id === event.triggerId);
      if (trigger) this.showTriggerToast(trigger);
    }
  }

  registerChart(instance) {
    if (!instance?.overlayCanvas || instance.alertInteractionsRegistered) return;
    instance.alertInteractionsRegistered = true;
    instance.overlayCanvas.addEventListener(
      "pointerdown",
      (event) => this.beginLineDrag(instance, event),
      { capture: true }
    );
    instance.overlayCanvas.addEventListener(
      "pointermove",
      (event) => this.updateLineDrag(instance, event),
      { capture: true }
    );
    instance.overlayCanvas.addEventListener(
      "pointerup",
      (event) => this.commitLineDrag(instance, event),
      { capture: true }
    );
    instance.overlayCanvas.addEventListener(
      "pointercancel",
      (event) => this.cancelLineDrag(instance, event),
      { capture: true }
    );
    this.syncChart(instance);
    if (this.localMode) this.bootstrapLocalCharts();
  }

  async refresh({ quiet = false } = {}) {
    this.refreshController?.abort();
    const controller = new AbortController();
    this.refreshController = controller;
    try {
      const [alerts, triggers] = await Promise.all([
        this.repository.listAll(),
        this.repository.history()
      ]);
      if (controller.signal.aborted) return;
      this.alerts = alerts;
      this.triggers = triggers;
      this.syncPolicyControls();
      if (this.localMode) this.syncLocalWorkerAlerts();
      this.syncCharts();
      this.renderManager();
      this.emitObjectTreeChange();
    } catch (error) {
      if (controller.signal.aborted) return;
      if (!quiet) this.showToast(error.message, { kind: "error" });
      this.setManagerState("error", error.message);
    } finally {
      if (this.refreshController === controller) this.refreshController = null;
    }
  }

  openManager(tab = "Active") {
    this.managerTab = tab === "Log" || tab === "Triggered" ? "Log" : "Active";
    this.managerElement.hidden = false;
    if (!this.managerHasOpened) {
      const width = Math.min(320, this.surface.clientWidth);
      const height = Math.min(720, this.surface.clientHeight);
      this.managerFloating.applyGeometry({
        x: Math.max(0, Math.min(170, this.surface.clientWidth - width)),
        y: Math.max(0, Math.min(70, this.surface.clientHeight - height)),
        width,
        height
      });
      this.managerHasOpened = true;
    }
    this.setFocusedAlertWindow(this.managerElement);
    this.managerFloating.restore();
    this.managerFloating.constrainToSurface();
    this.renderManager();
    this.managerElement.querySelector("[data-alert-search]")?.focus({ preventScroll: true });
    this.refresh({ quiet: true });
    this.emitObjectTreeChange();
  }

  async openEditor({
    instance = this.getActiveChart(),
    price = null,
    drawing = null,
    alert = null,
    indicatorContext = null,
    indicatorBContext = null
  } = {}) {
    const linked = alert || (
      drawing ? this.alerts.find((item) => item.sourceDrawingId === drawing.id) : null
    );
    if (!linked) {
      const disabledReason = this.alertCreationDisabledReason();
      if (disabledReason) {
        this.showToast(disabledReason, { kind: "error" });
        return;
      }
    }
    if (!instance?.symbolId) {
      this.showToast("Choose a supported symbol before creating an alert.", { kind: "error" });
      return;
    }
    if (
      linked &&
      ["IndicatorValue", "PriceIndicator", "IndicatorPair"].includes(linked.type) &&
      !indicatorContext
    ) {
      this.openExistingIndicatorEditor(instance, linked);
      return;
    }
    this.editorIndicator = indicatorContext;
    this.editorIndicatorB = indicatorBContext;
    try {
      await this.prepareEditorIndicatorChoices(instance, [
        indicatorContext,
        indicatorBContext
      ]);
    } catch (error) {
      this.editorIndicatorChoices = [];
      this.editorIndicatorChoicesByKey.clear();
      if (indicatorContext || indicatorBContext) {
        this.showToast(error.message, { kind: "error" });
        return;
      }
    }
    const currentPrice = lastPrice(instance);
    const drawingPrice = Number(drawing?.anchors?.[0]?.price);
    const threshold = finitePrice(price)
      ?? finitePrice(drawingPrice)
      ?? finitePrice(currentPrice)
      ?? 1;
    const operator = linked?.operator || (
      Number.isFinite(currentPrice) && threshold < currentPrice
        ? "CrossesBelow"
        : "CrossesAbove"
    );
    const sourceChartId = rootChartId(this.root);
    const data = {
      id: linked?.id || "",
      version: linked?.version || 0,
      symbol: instance.symbol,
      symbolId: instance.symbolId,
      type: drawing ? "HorizontalDrawing" : linked?.type || (
        indicatorContext ? "IndicatorValue" : "FixedPrice"
      ),
      operator,
      thresholdPrice: linked?.thresholdPrice ?? threshold,
      timeframe: linked?.timeframe || instance.timeframe || "15m",
      triggerTiming: linked?.triggerTiming || "Immediate",
      frequency: linked?.frequency || "EveryOccurrence",
      session: linked?.session || "Regular",
      expiration: toLocalDateTimeValue(linked?.expiresAtUtc),
      name: linked?.name || (
        indicatorContext
          ? generatedIndicatorName(
              instance.symbol,
              indicatorContext.capability.displayName,
              operator,
              threshold)
          : generatedName(instance.symbol, operator, threshold)
      ),
      customMessage: linked?.customMessage || "",
      sourceChartId: linked?.sourceChartId || sourceChartId,
      sourceDrawingId: drawing?.id || linked?.sourceDrawingId || "",
      indicator: indicatorContext,
      indicatorB: indicatorBContext,
      calculationReadiness: linked?.calculationReadiness || "Initializing"
    };
    this.editorAlert = linked;
    this.editorDrawing = drawing;
    this.editorInstance = instance;
    this.populateEditor(data);
    this.editorElement.hidden = false;
    if (!this.editorHasOpened) {
      const width = Math.min(440, this.surface.clientWidth);
      const height = Math.min(728, this.surface.clientHeight);
      this.editorFloating.applyGeometry({
        x: Math.max(0, Math.min(300, this.surface.clientWidth - width)),
        y: Math.max(0, Math.min(100, this.surface.clientHeight - height)),
        width,
        height
      });
      this.editorHasOpened = true;
    }
    this.setFocusedAlertWindow(this.editorElement);
    this.editorFloating.restore();
    this.editorFloating.constrainToSurface();
    this.editorElement.querySelector("[name='operator']")?.focus({ preventScroll: true });
    trapFocus(this.editorElement);
    this.emitObjectTreeChange();
  }

  async prepareEditorIndicatorChoices(instance, snapshots = []) {
    const chartIndicators = (instance?.indicatorInstances || [])
      .filter((indicator) => indicator.visible !== false);
    const existingSnapshots = snapshots.filter(Boolean);
    if (chartIndicators.length === 0 && existingSnapshots.length === 0) {
      this.editorIndicatorChoices = [];
      this.editorIndicatorChoicesByKey.clear();
      return;
    }
    this.indicatorCatalog ||= await this.repository.capabilities();
    const catalog = this.indicatorCatalog?.indicators || [];
    const choices = [];
    for (const indicator of chartIndicators) {
      const definition = getIndicatorDefinition(indicator.definitionId);
      const capability = catalog.find(
        (item) =>
          item.id === indicator.definitionId &&
          item.isAvailable &&
          !item.isDeprecated
      );
      const context = capability && definition
        ? editorIndicatorFromChart(indicator, definition, capability, this.indicatorVersions)
        : null;
      choices.push({
        key: `indicator:${indicator.id}`,
        label: chartIndicatorLabel(indicator, definition),
        context,
        supported: Boolean(context)
      });
    }
    for (const [index, snapshot] of existingSnapshots.entries()) {
      const matching = choices.find(
        (choice) =>
          snapshot.resourceId &&
          choice.context?.resourceId === snapshot.resourceId
      );
      if (matching) {
        matching.context = cloneEditorIndicatorContext(snapshot);
        matching.supported = true;
        continue;
      }
      const key = snapshot.resourceId
        ? `indicator:${snapshot.resourceId}`
        : `snapshot:${index}`;
      choices.push({
        key,
        label: `${snapshot.definition?.abbreviation
          || snapshot.capability.displayName} (alert snapshot)`,
        context: cloneEditorIndicatorContext(snapshot),
        supported: true
      });
    }
    this.editorIndicatorChoices = choices;
    this.editorIndicatorChoicesByKey = new Map(
      choices.map((choice) => [choice.key, choice])
    );
  }

  openForDrawing(instance, drawing) {
    if (drawing?.type !== "horizontal-line") return false;
    this.openEditor({ instance, drawing });
    return true;
  }

  async openForIndicator(instance, indicator, definition) {
    if (!instance?.symbolId || !indicator || !definition) return false;
    try {
      const catalog = await this.repository.capabilities();
      this.indicatorCatalog = catalog;
      const capability = (catalog.indicators || []).find(
        (item) => item.id === definition.id && item.isAvailable && !item.isDeprecated
      );
      if (!capability) {
        this.showToast(
          `${definition.name} is not available for ${
            this.localMode ? "local" : "server-side"
          } alerts.`,
          { kind: "error" }
        );
        return false;
      }
      const output = capability.outputs.find((item) =>
        definition.outputs.some((candidate) =>
          candidate.key === item.id && !["fill", "level"].includes(candidate.kind)))
        || capability.outputs[0];
      const parameters = Object.fromEntries(
        capability.parameters.map((parameter) => [
          parameter.id,
          String(indicator.inputs?.[parameter.id] ?? parameter.defaultValue)
        ])
      );
      const context = {
        instance: indicator,
        definition,
        capability,
        resourceId: indicator.id,
        resourceVersion: this.indicatorVersions.get(indicator.id) ?? null,
        parameters,
        inputSeries: String(indicator.inputs?.source || capability.supportedInputSeries[0]),
        outputSeries: output.id,
        adjustmentMode: "Unadjusted"
      };
      const value = lastIndicatorValue(indicator, output.id);
      await this.openEditor({
        instance,
        price: Number.isFinite(value) ? value : lastPrice(instance),
        indicatorContext: context
      });
      await this.previewEditorIndicator();
      return true;
    } catch (error) {
      this.showToast(error.message, { kind: "error" });
      return false;
    }
  }

  async openExistingIndicatorEditor(instance, alert) {
    try {
      const catalog = await this.repository.capabilities();
      this.indicatorCatalog = catalog;
      const capability = (catalog.indicators || []).find(
        (item) =>
          item.id === alert.indicatorId &&
          item.algorithmVersion === alert.indicatorAlgorithmVersion
      );
      if (!capability) {
        this.showToast(
          "This alert uses an indicator algorithm version that is no longer available.",
          { kind: "error" }
        );
        return;
      }
      const chartIndicator = instance?.indicatorInstances?.find(
        (item) => item.id === alert.sourceIndicatorId
      );
      const context = {
        instance: chartIndicator || null,
        definition: {
          id: alert.indicatorId,
          name: capability.displayName,
          abbreviation: capability.displayName
        },
        capability,
        resourceId: alert.sourceIndicatorId || "",
        resourceVersion: alert.sourceIndicatorVersion || null,
        parameters: parseParameters(alert.indicatorParametersJson),
        inputSeries: alert.indicatorInputSeries,
        outputSeries: alert.indicatorOutputSeries,
        adjustmentMode: alert.adjustmentMode || "Unadjusted"
      };
      let contextB = null;
      if (alert.type === "IndicatorPair") {
        const capabilityB = (catalog.indicators || []).find(
          (item) =>
            item.id === alert.indicatorBId &&
            item.algorithmVersion === alert.indicatorBAlgorithmVersion
        );
        if (!capabilityB) {
          this.showToast(
            "The second indicator algorithm version is no longer available.",
            { kind: "error" }
          );
          return;
        }
        const chartIndicatorB = instance?.indicatorInstances?.find(
          (item) => item.id === alert.sourceIndicatorBId
        );
        contextB = {
          instance: chartIndicatorB || null,
          definition: {
            id: alert.indicatorBId,
            name: capabilityB.displayName,
            abbreviation: capabilityB.displayName
          },
          capability: capabilityB,
          resourceId: alert.sourceIndicatorBId || "",
          resourceVersion: alert.sourceIndicatorBVersion || null,
          parameters: parseParameters(alert.indicatorBParametersJson),
          inputSeries: alert.indicatorBInputSeries,
          outputSeries: alert.indicatorBOutputSeries,
          adjustmentMode: alert.adjustmentMode || "Unadjusted"
        };
      }
      await this.openEditor({
        instance,
        alert,
        indicatorContext: context,
        indicatorBContext: contextB
      });
      await this.previewEditorIndicator();
    } catch (error) {
      this.showToast(error.message, { kind: "error" });
    }
  }

  openAlertContextMenu(instance, event) {
    const hit = this.alertAtPoint(instance, event);
    if (!hit) return false;
    return this.showAlertContextMenu(hit, instance, event);
  }

  showAlertContextMenu(hit, instance, event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.contextAlert = hit;
    this.contextInstance = instance;
    this.contextMenu.replaceChildren();
    const actions = [
      ["edit", "Edit alert"],
      [hit.status === "Paused" ? "resume" : "pause", hit.status === "Paused" ? "Resume" : "Pause"],
      ["duplicate", "Duplicate"],
      ...(hit.status === "Triggered" ? [["rearm", "Rearm"]] : []),
      ["manager", "Open in Alert Manager"],
      ["delete", "Delete"]
    ];
    for (const [action, label] of actions) {
      const button = document.createElement("button");
      button.type = "button";
      button.role = "menuitem";
      button.dataset.alertContextAction = action;
      button.textContent = label;
      if (["duplicate", "resume", "rearm"].includes(action)) {
        const reason = this.alertCreationDisabledReason(
          alertActionAdditionalSlots(alert, action)
        );
        button.disabled = Boolean(reason);
        if (reason) button.title = reason;
      }
      this.contextMenu.append(button);
    }
    this.contextMenu.hidden = false;
    const surfaceRect = this.surface.getBoundingClientRect();
    const x = event.clientX - surfaceRect.left;
    const y = event.clientY - surfaceRect.top;
    this.contextMenu.style.left = `${Math.max(6, Math.min(
      x,
      surfaceRect.width - this.contextMenu.offsetWidth - 6
    ))}px`;
    this.contextMenu.style.top = `${Math.max(6, Math.min(
      y,
      surfaceRect.height - this.contextMenu.offsetHeight - 6
    ))}px`;
    this.contextMenu.querySelector("button")?.focus();
    return true;
  }

  openManagerAlertContextMenu(event) {
    const row = event.target.closest("[data-alert-id]");
    if (!row) return false;
    const alert = this.alerts.find((item) => item.id === row.dataset.alertId);
    if (!alert) return false;
    return this.showAlertContextMenu(
      alert,
      this.chartForAlert(alert) || this.getActiveChart(),
      event
    );
  }

  openAtChartPoint(instance, price = null) {
    this.openEditor({ instance, price });
  }

  async handleDrawingCommand(instance, before, after) {
    const beforeById = new Map((before?.drawings?.drawings || []).map((item) => [item.id, item]));
    const afterById = new Map((after?.drawings?.drawings || []).map((item) => [item.id, item]));
    for (const alert of this.alerts.filter((item) =>
      item.type === "HorizontalDrawing" &&
      item.sourceDrawingId &&
      item.status !== "Triggered")) {
      const oldDrawing = beforeById.get(alert.sourceDrawingId);
      const newDrawing = afterById.get(alert.sourceDrawingId);
      const oldPrice = Number(oldDrawing?.anchors?.[0]?.price);
      const newPrice = Number(newDrawing?.anchors?.[0]?.price);
      if (!Number.isFinite(newPrice) || newPrice === oldPrice) continue;
      try {
        const resource = await this.repository.upsertDrawing(
          alert.sourceChartId,
          alert.sourceDrawingId,
          snapPrice(newPrice, alert.tickSize, alert.pricePrecision),
          this.drawingVersions.get(alert.sourceDrawingId) ?? null
        );
        this.drawingVersions.set(alert.sourceDrawingId, resource.version);
        await this.repository.update(alert, updatePayload(alert, {
          thresholdPrice: snapPrice(newPrice, alert.tickSize, alert.pricePrecision)
        }));
        await this.refresh({ quiet: true });
      } catch (error) {
        instance.workspace.restoreDrawingState(before);
        this.showToast(
          `The drawing moved, but its alert could not be updated: ${error.message}`,
          { kind: "error" }
        );
      }
    }
  }

  async handleIndicatorDeleted(instance, indicator) {
    const linked = this.alerts.filter(
      (alert) => alert.sourceIndicatorId === indicator.id && alert.sourceChartId
    );
    if (linked.length === 0) return;
    const chartId = linked[0].sourceChartId;
    const expectedVersion = this.indicatorVersions.get(indicator.id)
      ?? Math.max(...linked.map((alert) => Number(alert.sourceIndicatorVersion || 0)));
    try {
      await this.repository.deleteIndicator(chartId, indicator.id, expectedVersion);
      this.indicatorVersions.delete(indicator.id);
      this.showToast(
        `${indicator.displayName} was removed; ${linked.length} alert${linked.length === 1 ? "" : "s"} will continue as standalone server calculations.`
      );
      await this.refresh({ quiet: true });
    } catch (error) {
      this.showToast(
        `The indicator was removed from the chart; its alert snapshot remains active. ${error.message}`,
        { kind: "error" }
      );
    }
  }

  requestDrawingDelete(instance, drawing, deleteDrawing) {
    const linked = this.alerts.find((item) => item.sourceDrawingId === drawing.id);
    if (!linked) {
      deleteDrawing();
      return false;
    }
    this.pendingDrawingDelete = { instance, drawing, alert: linked, deleteDrawing };
    this.deleteDialog.hidden = false;
    this.deleteDialog.querySelector("[data-delete-drawing-alert]")?.focus();
    trapFocus(this.deleteDialog);
    return true;
  }

  syncCharts() {
    for (const chart of this.getCharts()) this.syncChart(chart);
  }

  syncChart(chart) {
    if (!chart?.workspace) return;
    const symbolId = String(chart.symbolId || "").toLowerCase();
    const alerts = this.alerts.filter((item) =>
      String(item.symbolId || "").toLowerCase() === symbolId);
    const ids = new Set(alerts.map((item) => item.id));
    const triggers = this.triggers.filter((item) =>
      ids.has(item.alertId) ||
      String(item.symbolId || "").toLowerCase() === symbolId);
    chart.workspace.setAlerts(alerts, triggers);
    const linkedByDrawing = new Map(
      alerts
        .filter((item) => item.sourceDrawingId)
        .map((item) => [item.sourceDrawingId, item.id])
    );
    for (const drawing of chart.drawings || []) {
      const nextAlertId = linkedByDrawing.get(drawing.id) || null;
      if ((drawing.metadata?.alertId || null) !== nextAlertId) {
        chart.workspace.updateDrawingMetadata(drawing.id, { alertId: nextAlertId });
      }
    }
  }

  createManagerWindow() {
    const element = document.createElement("section");
    element.className = "chart-floating-window alert-manager-window";
    element.dataset.alertManager = "";
    element.dataset.windowSnapId = "alert-manager";
    element.setAttribute("role", "dialog");
    element.setAttribute("aria-label", "Alert Manager");
    element.hidden = true;
    element.innerHTML = `
      <header class="chart-window-titlebar alert-window-titlebar" data-alert-manager-drag-handle>
        <div><strong>Alert Manager</strong><span>${
          this.localMode ? "Local &middot; active while open" : "Server-side alerts"
        }</span></div>
        <button type="button" data-alert-manager-close aria-label="Close Alert Manager" title="Close">&times;</button>
      </header>
      <div class="alert-manager-toolbar">
        <button type="button" data-alert-create aria-label="New alert" title="New alert">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
        </button>
        <label class="alert-search-control"><span class="chart-sr-only">Search alerts</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>
          <input type="search" data-alert-search placeholder="Search alerts&hellip;" />
        </label>
        <button type="button" class="alert-sort-control" data-alert-sort-button aria-label="Sort alerts" title="Sort alerts" aria-haspopup="listbox" aria-expanded="false" aria-controls="alert-sort-menu">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 6h10M8 12h7M8 18h4"/><path d="m4 5-2 2 2 2M2 7v10M2 17l2-2"/></svg>
        </button>
        <div class="alert-sort-menu" data-alert-sort-menu id="alert-sort-menu" role="listbox" aria-label="Sort alerts" hidden>
          <button type="button" role="option" data-alert-sort-value="updated">Recently updated</button>
          <button type="button" role="option" data-alert-sort-value="symbol">Symbol</button>
          <button type="button" role="option" data-alert-sort-value="threshold">Threshold</button>
          <button type="button" role="option" data-alert-sort-value="triggered">Last triggered</button>
        </div>
      </div>
      <div class="alert-manager-tabs" role="tablist" aria-label="Alert status"></div>
      <div class="alert-bulk-bar" data-alert-bulk-bar hidden>
        <span data-alert-selected-count></span>
        <button type="button" data-alert-bulk-action="pause">Pause</button>
        <button type="button" data-alert-bulk-action="resume">Resume</button>
        <button type="button" data-alert-bulk-action="rearm">Rearm</button>
        <button type="button" data-alert-bulk-action="delete">Delete</button>
      </div>
      <div class="alert-manager-state" data-alert-manager-state role="status"></div>
      <div class="alert-manager-list" data-alert-manager-list></div>
      ${resizeHandles()}
    `;
    this.surface.append(element);
    this.managerElement = element;
    this.managerList = element.querySelector("[data-alert-manager-list]");
    const tabs = element.querySelector(".alert-manager-tabs");
    for (const tab of MANAGER_TABS) {
      const button = document.createElement("button");
      button.type = "button";
      button.role = "tab";
      button.dataset.alertTab = tab;
      button.textContent = tab;
      tabs.append(button);
    }
    this.managerFloating = new FloatingWindowController({
      surface: this.surface,
      windowElement: element,
      dragHandle: element.querySelector("[data-alert-manager-drag-handle]"),
      resizeHandles: element.querySelectorAll("[data-window-resize]"),
      onInteractionEnd: () => this.onWindowStateChange()
    });
    this.managerFloating.reset({ offsetX: 170, offsetY: 70 });
    element.hidden = true;
  }

  createEditorWindow() {
    const element = document.createElement("section");
    element.className = "chart-floating-window alert-editor-window";
    element.dataset.alertEditor = "";
    element.dataset.windowSnapId = "alert-editor";
    element.setAttribute("role", "dialog");
    element.setAttribute("aria-modal", "true");
    element.setAttribute("aria-labelledby", "alert-editor-title");
    element.hidden = true;
    element.innerHTML = `
      <header class="chart-window-titlebar alert-window-titlebar" data-alert-editor-drag-handle>
        <div class="alert-editor-title-group">
          <strong id="alert-editor-title" data-alert-editor-title>Create alert</strong>
          <div class="alert-editor-title-meta">
            <span data-alert-editor-symbol></span>
            <label class="alert-editor-timeframe">
              <span class="chart-sr-only">Timeframe</span>
              <select name="timeframe" form="alert-editor-form" aria-label="Timeframe" data-chart-dropdown>
                ${SUPPORTED_CHART_TIMEFRAMES.map((timeframe) =>
                  `<option value="${timeframe}">${timeframe}</option>`).join("")}
              </select>
            </label>
          </div>
        </div>
        <button type="button" data-alert-editor-close aria-label="Close alert editor" title="Close">&times;</button>
      </header>
      <form class="alert-editor-form" data-alert-form id="alert-editor-form" novalidate>
        <div class="alert-editor-error" data-alert-form-error role="alert" hidden></div>
        <div class="alert-source-control">
          <span class="alert-field-title">Alert source</span>
          <div class="alert-source-fields">
            <label>
              <span class="chart-sr-only">Alert source</span>
              <select name="alertSource" aria-label="Alert source" data-chart-dropdown></select>
            </label>
            <label data-alert-source-series-field hidden>
              <span class="chart-sr-only">Source series</span>
              <select name="sourceSeries" aria-label="Source series" data-chart-dropdown></select>
            </label>
          </div>
        </div>
        <div class="alert-editor-control-row">
          <label>Condition
            <select name="operator" aria-label="Condition" data-chart-dropdown>${OPERATORS.map(([value, label]) =>
              `<option value="${value}">${label}</option>`).join("")}</select>
          </label>
        </div>
        <div class="alert-target-control">
          <span class="alert-field-title">Target</span>
          <div class="alert-target-fields">
            <label>
              <span class="chart-sr-only">Target type</span>
              <select name="targetType" aria-label="Target" data-chart-dropdown></select>
            </label>
            <label data-alert-threshold-field>
              <span class="chart-sr-only" data-alert-threshold-label>Target value</span>
              <input name="thresholdPrice" aria-label="Target value" type="number" min="0.0000000001" step="any" inputmode="decimal" required />
            </label>
            <label data-alert-target-series-field hidden>
              <span class="chart-sr-only">Target value</span>
              <select name="targetSeries" aria-label="Target series" data-chart-dropdown></select>
            </label>
          </div>
          <small data-alert-price-help></small>
        </div>
        <section class="alert-indicator-fields alert-indicator-status" data-alert-indicator-fields hidden>
          <p class="alert-indicator-preview" data-alert-indicator-preview role="status">
            Select an indicator series to preview its current canonical value.
          </p>
        </section>
        <label>Alert name
          <input name="name" type="text" maxlength="160" required />
        </label>
        <label>Custom message <span>Optional</span>
          <textarea name="customMessage" maxlength="1000" rows="2"></textarea>
        </label>
        <section class="alert-behavior-options" aria-label="Alert behavior">
          <div class="alert-option-row" role="group" aria-labelledby="alert-trigger-timing-label">
            <span class="alert-option-label" id="alert-trigger-timing-label">Trigger timing</span>
            <div class="alert-segmented-options">
              <label><input name="triggerTiming" type="radio" value="Immediate" /><span>Immediate</span></label>
              <label><input name="triggerTiming" type="radio" value="CompletedBar" /><span>Completed bar</span></label>
            </div>
          </div>
          <div class="alert-option-row" role="group" aria-labelledby="alert-frequency-label">
            <span class="alert-option-label" id="alert-frequency-label">Frequency</span>
            <div class="alert-segmented-options">
              <label><input name="frequency" type="radio" value="EveryOccurrence" /><span>Every time</span></label>
              <label><input name="frequency" type="radio" value="OncePerBar" /><span>Once / bar</span></label>
              <label><input name="frequency" type="radio" value="Once" /><span>Once</span></label>
            </div>
          </div>
          <div class="alert-option-row" role="group" aria-labelledby="alert-session-label">
            <span class="alert-option-label" id="alert-session-label">Session</span>
            <div class="alert-segmented-options">
              <label><input name="session" type="radio" value="Regular" /><span>Regular</span></label>
              <label><input name="session" type="radio" value="RegularAndExtended" /><span>Extended</span></label>
            </div>
          </div>
        </section>
        <div class="alert-expiration-field" data-alert-expiration>
          <span class="alert-field-title">Expiration <small>Optional</small></span>
          <button type="button" class="alert-expiration-toggle" data-alert-expiration-toggle aria-haspopup="dialog" aria-expanded="false">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2"/><path d="M8 3v4M16 3v4M4 10h16"/></svg>
            <span data-alert-expiration-label>No expiration</span>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 10 4 4 4-4"/></svg>
          </button>
          <input name="expiration" type="hidden" />
          <div class="alert-expiration-popover" data-alert-expiration-popover role="dialog" aria-label="Choose expiration" hidden>
            <div class="alert-expiration-navigation">
              <button type="button" data-alert-expiration-previous aria-label="Previous month"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg></button>
              <strong data-alert-expiration-heading></strong>
              <button type="button" data-alert-expiration-next aria-label="Next month"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></button>
            </div>
            <div class="alert-expiration-weekdays" aria-hidden="true">
              <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
            </div>
            <div class="alert-expiration-calendar" data-alert-expiration-calendar></div>
            <label class="alert-expiration-time">Time
              <input type="time" data-alert-expiration-time value="16:00" />
            </label>
            <footer>
              <button type="button" data-alert-expiration-clear>Clear</button>
              <button type="button" class="is-primary" data-alert-expiration-done>Done</button>
            </footer>
          </div>
        </div>
        <footer class="alert-editor-actions">
          <button type="button" class="is-secondary" data-alert-editor-cancel>Cancel</button>
          <button type="button" class="is-primary" data-alert-submit>Create alert</button>
        </footer>
      </form>
      ${resizeHandles()}
    `;
    this.surface.append(element);
    this.editorElement = element;
    this.editorForm = element.querySelector("[data-alert-form]");
    this.expirationPicker = new AlertExpirationPicker(
      element.querySelector("[data-alert-expiration]")
    );
    this.editorDropdowns = enhanceChartDropdowns(element);
    this.editorFloating = new FloatingWindowController({
      surface: this.surface,
      windowElement: element,
      dragHandle: element.querySelector("[data-alert-editor-drag-handle]"),
      resizeHandles: element.querySelectorAll("[data-window-resize]")
    });
    this.editorFloating.reset({ offsetX: 300, offsetY: 100 });
    element.hidden = true;
  }

  createAlertContextMenu() {
    const menu = document.createElement("div");
    menu.className = "chart-context-menu alert-context-menu";
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", "Alert actions");
    menu.hidden = true;
    this.surface.append(menu);
    this.contextMenu = menu;
  }

  createDrawingDeleteDialog() {
    const layer = document.createElement("div");
    layer.className = "alert-confirm-layer";
    layer.hidden = true;
    layer.innerHTML = `
      <section role="dialog" aria-modal="true" aria-labelledby="delete-linked-alert-title">
        <h2 id="delete-linked-alert-title">This line has an alert</h2>
        <p>Choose what should happen to the linked alert.</p>
        <button type="button" data-delete-drawing-alert>Delete drawing and alert</button>
        <button type="button" data-delete-drawing-preserve>Delete drawing, preserve fixed-price alert</button>
        <button type="button" data-delete-drawing-cancel>Cancel</button>
      </section>
    `;
    this.surface.append(layer);
    this.deleteDialog = layer;
  }

  createToast() {
    const toast = document.createElement("div");
    toast.className = "alert-toast";
    toast.setAttribute("role", "status");
    toast.hidden = true;
    this.surface.append(toast);
    this.toast = toast;
  }

  bind() {
    const focusListenerOptions = { signal: this.focusListeners.signal };
    document.addEventListener(
      "pointerdown",
      (event) => this.syncAlertWindowFocus(event.target),
      { ...focusListenerOptions, capture: true }
    );
    document.addEventListener(
      "focusin",
      (event) => this.syncAlertWindowFocus(event.target),
      focusListenerOptions
    );
    this.openButton?.addEventListener("click", () => this.openManager());
    this.managerElement.querySelector("[data-alert-manager-close]")
      .addEventListener("click", () => this.closeManager());
    this.managerElement.querySelector("[data-alert-create]")
      .addEventListener("click", () => this.openEditor());
    this.managerElement.querySelector("[data-alert-search]")
      .addEventListener("input", (event) => {
        this.managerSearch = event.target.value;
        this.renderManager();
      });
    this.managerElement.querySelector("[data-alert-sort-button]")
      .addEventListener("click", () => {
        this.managerSortOpen = !this.managerSortOpen;
        this.renderManager();
      });
    this.managerElement.querySelector("[data-alert-sort-menu]")
      .addEventListener("click", (event) => {
        const option = event.target.closest("[data-alert-sort-value]");
        if (!option) return;
        this.managerSort = option.dataset.alertSortValue;
        this.managerSortOpen = false;
        this.renderManager();
        this.onWindowStateChange();
        this.managerElement.querySelector("[data-alert-sort-button]")?.focus();
      });
    this.managerElement.querySelector("[data-alert-sort-menu]")
      .addEventListener("keydown", (event) => {
        if (event.key !== "Escape") return;
        this.managerSortOpen = false;
        this.renderManager();
        this.managerElement.querySelector("[data-alert-sort-button]")?.focus();
      });
    this.managerElement.querySelector(".alert-manager-tabs")
      .addEventListener("click", (event) => {
        const button = event.target.closest("[data-alert-tab]");
        if (!button) return;
        this.managerTab = button.dataset.alertTab;
        this.managerSortOpen = false;
        this.selectedIds.clear();
        this.renderManager();
        this.onWindowStateChange();
      });
    this.managerList.addEventListener("click", (event) => this.handleManagerClick(event));
    this.managerList.addEventListener(
      "contextmenu",
      (event) => this.openManagerAlertContextMenu(event)
    );
    this.managerList.addEventListener("change", (event) => this.handleManagerSelection(event));
    this.managerElement.querySelector("[data-alert-bulk-bar]")
      .addEventListener("click", (event) => this.handleBulkAction(event));

    this.editorElement.querySelector("[data-alert-editor-close]")
      .addEventListener("click", () => this.closeEditor());
    this.editorElement.querySelector("[data-alert-editor-cancel]")
      .addEventListener("click", () => this.closeEditor());
    this.editorElement.querySelector("[data-alert-submit]")
      .addEventListener("click", (event) => this.submitEditor(event));
    this.editorForm.addEventListener("keydown", (event) => {
      const target = event.target;
      const submitsFromField =
        target instanceof HTMLInputElement &&
        ["text", "number", "search"].includes(target.type);
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        submitsFromField
      ) {
        event.preventDefault();
        this.submitEditor(event);
      }
    });
    this.editorForm.elements.operator.addEventListener("change", () => {
      this.syncEditorTargetControls();
      if (this.editorIndicator) {
        this.previewEditorIndicator();
      } else {
        this.refreshGeneratedName();
      }
    });
    this.editorForm.elements.thresholdPrice.addEventListener("input", () => {
      this.refreshGeneratedName();
      if (!this.editorIndicator) return;
      window.clearTimeout(this.editorPreviewTimer);
      this.editorPreviewTimer = window.setTimeout(
        () => this.previewEditorIndicator(),
        150
      );
    });
    this.editorForm.elements.thresholdPrice.addEventListener("change", () => {
      if (this.editorIndicator) this.previewEditorIndicator();
    });
    this.editorForm.elements.name.addEventListener("input", () => {
      this.editorNameEdited = true;
    });
    for (const input of this.editorForm.querySelectorAll("[name='triggerTiming']")) {
      input.addEventListener("change", () => {
        this.syncTimingControls();
        if (this.editorIndicator) this.previewEditorIndicator();
      });
    }
    this.editorForm.elements.alertSource.addEventListener(
      "change",
      () => this.changeEditorSource()
    );
    this.editorForm.elements.sourceSeries.addEventListener(
      "change",
      () => this.changeEditorSourceSeries()
    );
    this.editorForm.elements.targetType.addEventListener(
      "change",
      () => this.changeEditorTarget()
    );
    this.editorForm.elements.targetSeries.addEventListener(
      "change",
      () => this.selectEditorTargetSeries()
    );

    this.contextMenu.addEventListener("click", (event) => this.handleContextAction(event));
    this.contextMenu.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.closeAlertContextMenu(true);
    });
    document.addEventListener("pointerdown", (event) => {
      if (!this.contextMenu.hidden && !this.contextMenu.contains(event.target)) {
        this.closeAlertContextMenu();
      }
      if (
        this.managerSortOpen &&
        !event.target.closest("[data-alert-sort-button], [data-alert-sort-menu]")
      ) {
        this.managerSortOpen = false;
        this.renderManager();
      }
    });

    this.deleteDialog.querySelector("[data-delete-drawing-cancel]")
      .addEventListener("click", () => this.closeDrawingDeleteDialog());
    this.deleteDialog.querySelector("[data-delete-drawing-alert]")
      .addEventListener("click", () => this.deleteDrawingAndAlert());
    this.deleteDialog.querySelector("[data-delete-drawing-preserve]")
      .addEventListener("click", () => this.preserveAlertAndDeleteDrawing());
  }

  populateEditor(data) {
    const form = this.editorForm;
    form.reset();
    const sourceIndicator = ["IndicatorValue", "IndicatorPair"].includes(data.type);
    const targetIndicator = ["PriceIndicator", "IndicatorPair"].includes(data.type);
    this.editorSourceMode = sourceIndicator ? "indicator" : "price";
    this.editorTargetMode = targetIndicator ? "indicator" : "price";
    form.elements.thresholdPrice.value = formatPrice(data.thresholdPrice, 8, false);
    form.elements.name.value = data.name;
    form.elements.customMessage.value = data.customMessage;
    form.elements.triggerTiming.value = data.triggerTiming;
    form.elements.timeframe.value = data.timeframe;
    form.elements.frequency.value = data.frequency;
    form.elements.session.value = data.session;
    this.expirationPicker.setValue(data.expiration);
    form.dataset.symbol = data.symbol;
    form.dataset.symbolId = data.symbolId;
    form.dataset.type = data.type;
    form.dataset.sourceChartId = data.sourceChartId;
    form.dataset.sourceDrawingId = data.sourceDrawingId;
    this.populateEditorSelections({
      sourceContext: sourceIndicator ? data.indicator : null,
      targetContext: targetIndicator
        ? sourceIndicator
          ? data.indicatorB
          : data.indicator
        : null
    });
    this.editorNameEdited = Boolean(this.editorAlert);
    this.editorElement.querySelector("[data-alert-editor-title]").textContent =
      this.editorAlert ? "Edit alert" : "Create alert";
    this.editorElement.querySelector("[data-alert-editor-symbol]").textContent =
      data.symbol;
    this.editorElement.querySelector("[data-alert-submit]").textContent =
      this.editorAlert ? "Save changes" : "Create alert";
    this.syncPolicyControls();
    this.syncEditorConditionOptions(data.operator);
    this.syncEditorTargetControls();
    this.clearEditorError();
    this.syncTimingControls();
    this.syncEditorDropdowns();
  }

  syncTimingControls() {
    this.editorForm.elements.timeframe.disabled = false;
  }

  populateEditorSelections({ sourceContext = null, targetContext = null } = {}) {
    const sourceSelect = this.editorForm.elements.alertSource;
    replaceSelectOptions(sourceSelect, [
      ["price", "Price"],
      ...this.editorIndicatorChoices.map((choice) => [
        choice.key,
        choice.supported ? choice.label : `${choice.label} (unavailable)`,
        !choice.supported
      ])
    ]);
    sourceSelect.value = sourceContext
      ? this.editorChoiceKeyForContext(sourceContext) || "price"
      : "price";
    this.renderSourceSeriesOptions(sourceContext?.outputSeries);
    this.renderEditorTargetOptions(
      targetContext ? this.editorChoiceKeyForContext(targetContext) : "price"
    );
    this.renderTargetSeriesOptions(targetContext?.outputSeries);
    this.assignEditorContextsFromControls();
    this.renderIndicatorEditorFields(this.editorIndicator);
    this.syncEditorDropdowns();
  }

  editorChoiceKeyForContext(context) {
    if (!context) return null;
    const resourceMatch = this.editorIndicatorChoices.find(
      (choice) =>
        context.resourceId &&
        choice.context?.resourceId === context.resourceId
    );
    if (resourceMatch) return resourceMatch.key;
    return this.editorIndicatorChoices.find(
      (choice) =>
        choice.context?.capability.id === context.capability.id &&
        sameIndicatorParameters(choice.context.parameters, context.parameters)
    )?.key || null;
  }

  editorChoice(value) {
    return this.editorIndicatorChoicesByKey.get(value) || null;
  }

  selectedSourceContext() {
    return this.editorChoice(this.editorForm.elements.alertSource.value)?.context || null;
  }

  renderSourceSeriesOptions(preferred = null) {
    const context = this.selectedSourceContext();
    const field = this.editorElement.querySelector("[data-alert-source-series-field]");
    const select = this.editorForm.elements.sourceSeries;
    field.hidden = !context;
    select.disabled = !context;
    if (!context) {
      replaceSelectOptions(select, []);
      return;
    }
    const outputs = context.capability.outputs.filter(
      (output) => output.supportsFixedValueComparison
    );
    replaceSelectOptions(
      select,
      outputs.map((output) => [output.id, output.displayName])
    );
    select.value = outputs.some((output) => output.id === preferred)
      ? preferred
      : outputs[0]?.id || "";
    syncChartDropdown(select);
  }

  targetOutputsForChoice(choice) {
    if (!choice?.context) return [];
    const source = this.selectedSourceContext();
    if (!source) {
      return choice.context.capability.outputs.filter(
        (output) => output.supportsPriceComparison
      );
    }
    const sourceOutput = source.capability.outputs.find(
      (output) => output.id === this.editorForm.elements.sourceSeries.value
    );
    return choice.context.capability.outputs.filter(
      (output) => indicatorOutputsCompatible(sourceOutput, output)
    );
  }

  renderEditorTargetOptions(preferred = null) {
    const select = this.editorForm.elements.targetType;
    replaceSelectOptions(select, [
      ["price", "Price"],
      ...this.editorIndicatorChoices.map((choice) => [
        choice.key,
        choice.supported ? choice.label : `${choice.label} (unavailable)`,
        !choice.supported || this.targetOutputsForChoice(choice).length === 0
      ])
    ]);
    const preferredOption = [...select.options].find(
      (option) => option.value === preferred && !option.disabled
    );
    select.value = preferredOption?.value || "price";
    syncChartDropdown(select);
  }

  renderTargetSeriesOptions(preferred = null) {
    const select = this.editorForm.elements.targetSeries;
    const choice = this.editorChoice(this.editorForm.elements.targetType.value);
    const outputs = this.targetOutputsForChoice(choice);
    replaceSelectOptions(
      select,
      outputs.map((output) => [output.id, output.displayName])
    );
    select.value = outputs.some((output) => output.id === preferred)
      ? preferred
      : outputs[0]?.id || "";
    syncChartDropdown(select);
  }

  assignEditorContextsFromControls() {
    const sourceChoice = this.editorChoice(this.editorForm.elements.alertSource.value);
    const targetChoice = this.editorChoice(this.editorForm.elements.targetType.value);
    const sourceContext = sourceChoice?.context
      ? cloneEditorIndicatorContext(sourceChoice.context)
      : null;
    const targetContext = targetChoice?.context
      ? cloneEditorIndicatorContext(targetChoice.context)
      : null;
    if (sourceContext) {
      sourceContext.outputSeries = this.editorForm.elements.sourceSeries.value;
      this.editorIndicator = sourceContext;
      if (targetContext) {
        targetContext.outputSeries = this.editorForm.elements.targetSeries.value;
        this.editorIndicatorB = targetContext;
      } else {
        this.editorIndicatorB = null;
      }
    } else if (targetContext) {
      targetContext.outputSeries = this.editorForm.elements.targetSeries.value;
      this.editorIndicator = targetContext;
      this.editorIndicatorB = null;
    } else {
      this.editorIndicator = null;
      this.editorIndicatorB = null;
    }
  }

  async changeEditorSource() {
    this.editorSourceMode = isIndicatorChoice(this.editorForm.elements.alertSource.value)
      ? "indicator"
      : "price";
    await this.syncEditorSelections();
  }

  async changeEditorSourceSeries() {
    await this.syncEditorSelections();
  }

  async changeEditorTarget() {
    this.editorTargetMode = isIndicatorChoice(this.editorForm.elements.targetType.value)
      ? "indicator"
      : "price";
    await this.syncEditorSelections();
  }

  async selectEditorTargetSeries() {
    await this.syncEditorSelections();
  }

  async syncEditorSelections() {
    try {
      const sourceSeries = this.editorForm.elements.sourceSeries.value;
      const targetSelection = this.editorForm.elements.targetType.value;
      const targetSeries = this.editorForm.elements.targetSeries.value;
      this.renderSourceSeriesOptions(sourceSeries);
      this.renderEditorTargetOptions(targetSelection);
      this.renderTargetSeriesOptions(targetSeries);
      this.assignEditorContextsFromControls();
      this.renderIndicatorEditorFields(this.editorIndicator);
      this.syncEditorConditionOptions();
      this.syncEditorTargetControls();
      this.syncTimingControls();
      this.syncEditorDropdowns();
      if (this.editorIndicator) await this.previewEditorIndicator();
      else this.refreshGeneratedName();
    } catch (error) {
      this.showEditorError(error.message);
    }
  }

  syncEditorDropdowns() {
    for (const select of this.editorElement.querySelectorAll(
      "select[data-chart-dropdown]"
    )) {
      syncChartDropdown(select);
    }
  }

  renderLegacyTargetSeriesOptions() {
    const select = this.editorForm.elements.targetSeries;
    const candidates = this.editorTargetCandidates();
    replaceSelectOptions(
      select,
      candidates.map(({ capability, output }) => [
        `${capability.id}:${output.id}`,
        `${capability.displayName} · ${output.displayName}`
      ])
    );
    const context = this.editorForm.elements.alertSource.value === "indicator"
      ? this.editorIndicatorB
      : this.editorIndicator;
    select.value = context ? `${context.capability.id}:${context.outputSeries}` : "";
  }

  selectLegacyEditorTargetSeries() {
    const [indicatorId, outputSeries] =
      this.editorForm.elements.targetSeries.value.split(":");
    const capability = (this.indicatorCatalog?.indicators || []).find(
      (item) => item.id === indicatorId
    );
    if (!capability) return;
    const sourceIndicator = this.editorForm.elements.alertSource.value === "indicator";
    const current = sourceIndicator ? this.editorIndicatorB : this.editorIndicator;
    const context = current?.capability.id === indicatorId
      ? current
      : editorIndicatorFromCapability(capability);
    context.outputSeries = outputSeries;
    if (sourceIndicator) {
      this.editorIndicatorB = context;
      this.renderIndicatorBEditorFields(context);
    } else {
      this.editorIndicator = context;
      this.renderIndicatorEditorFields(context);
    }
    this.syncEditorConditionOptions();
    this.syncEditorTargetControls();
    this.previewEditorIndicator();
  }

  selectEditorIndicator() {
    const capability = (this.indicatorCatalog?.indicators || []).find(
      (item) => item.id === this.editorForm.elements.indicatorId.value
    );
    if (!capability) return;
    this.editorIndicator = editorIndicatorFromCapability(capability);
    this.renderIndicatorEditorFields(this.editorIndicator);
    this.ensureEditorTargetContext();
    if (
      this.editorForm.elements.alertSource.value === "indicator" &&
      this.editorForm.elements.targetType.value === "indicator" &&
      this.editorIndicatorB
    ) {
      this.renderIndicatorBEditorFields(this.editorIndicatorB);
    }
    this.renderTargetSeriesOptions();
    this.syncEditorConditionOptions();
    this.previewEditorIndicator();
  }

  selectEditorIndicatorB() {
    const capability = (this.indicatorCatalog?.indicators || []).find(
      (item) => item.id === this.editorForm.elements.indicatorBId.value
    );
    if (!capability) return;
    this.editorIndicatorB = editorIndicatorFromCapability(capability);
    this.renderIndicatorBEditorFields(this.editorIndicatorB);
    this.renderTargetSeriesOptions();
    this.syncEditorConditionOptions();
    this.previewEditorIndicator();
  }

  renderIndicatorEditorFields(context) {
    const fields = this.editorElement.querySelector("[data-alert-indicator-fields]");
    fields.hidden = !context;
    const contexts = [context, this.editorIndicatorB].filter(Boolean);
    const immediate = this.editorForm.querySelector(
      "input[name='triggerTiming'][value='Immediate']"
    );
    const completed = this.editorForm.querySelector(
      "input[name='triggerTiming'][value='CompletedBar']"
    );
    const supportsImmediate =
      contexts.length === 0 || contexts.every((item) => item.capability.supportsImmediate);
    const supportsCompleted =
      contexts.length === 0 ||
      contexts.every((item) => item.capability.supportsCompletedBar);
    if (immediate) immediate.disabled = !supportsImmediate;
    if (completed) completed.disabled = !supportsCompleted;
    const selectedTiming = this.editorForm.querySelector(
      "input[name='triggerTiming']:checked"
    );
    if (selectedTiming?.disabled) {
      this.editorForm.elements.triggerTiming.value = supportsImmediate
        ? "Immediate"
        : "CompletedBar";
    }
    if (!context) return;
    const current = lastIndicatorValue(context.instance, context.outputSeries);
    fields.querySelector("[data-alert-indicator-preview]").textContent =
      Number.isFinite(current)
        ? `${context.capability.displayName} · ${
            context.capability.outputs.find(
              (output) => output.id === context.outputSeries
            )?.displayName || context.outputSeries
          } ${formatIndicatorValue(
            current,
            context.capability,
            context.outputSeries
          )} · ${this.localMode ? "local validation pending" : "server validation pending"}`
        : `${context.capability.displayName} is initializing ${
            this.localMode ? "in this chart" : "on the server"
          }.`;
  }

  renderLegacyIndicatorEditorFields(context) {
    const fields = this.editorElement.querySelector("[data-alert-indicator-fields]");
    fields.hidden = !context;
    if (!context) return;
    const sourceIndicator = this.editorForm.elements.alertSource.value === "indicator";
    fields.querySelector("[data-alert-indicator-role]").textContent =
      sourceIndicator ? "Source indicator" : "Target indicator settings";
    fields.querySelector("[data-alert-primary-selector]").hidden = !sourceIndicator;
    const capabilities = (this.indicatorCatalog?.indicators || [])
      .filter((item) => item.isAvailable && !item.isDeprecated);
    replaceSelectOptions(
      this.editorForm.elements.indicatorId,
      capabilities.map((item) => [item.id, item.displayName])
    );
    this.editorForm.elements.indicatorId.value = context.capability.id;
    const immediate = this.editorForm.querySelector(
      "input[name='triggerTiming'][value='Immediate']"
    );
    const completed = this.editorForm.querySelector(
      "input[name='triggerTiming'][value='CompletedBar']"
    );
    if (immediate) immediate.disabled = !context.capability.supportsImmediate;
    if (completed) completed.disabled = !context.capability.supportsCompletedBar;
    const selectedTiming = this.editorForm.querySelector(
      "input[name='triggerTiming']:checked"
    );
    if (selectedTiming?.disabled) {
      this.editorForm.elements.triggerTiming.value =
        context.capability.supportsImmediate ? "Immediate" : "CompletedBar";
    }
    replaceSelectOptions(
      this.editorForm.elements.indicatorOutput,
      context.capability.outputs
        .filter((output) => output.supportsFixedValueComparison)
        .map((output) => [output.id, output.displayName])
    );
    this.editorForm.elements.indicatorOutput.value = context.outputSeries;
    const parameterRoot = fields.querySelector("[data-alert-indicator-parameters]");
    parameterRoot.replaceChildren();
    for (const parameter of context.capability.parameters) {
      const label = document.createElement("label");
      label.textContent = parameter.displayName;
      let input;
      if (parameter.options) {
        input = document.createElement("select");
        replaceSelectOptions(input, Object.entries(parameter.options));
      } else {
        input = document.createElement("input");
        input.type = parameter.type === "Boolean" ? "checkbox" : "number";
        if (parameter.minimum !== null) input.min = parameter.minimum;
        if (parameter.maximum !== null) input.max = parameter.maximum;
        if (parameter.step !== null) input.step = parameter.step;
      }
      input.dataset.indicatorParameter = parameter.id;
      const value = context.parameters?.[parameter.id] ?? parameter.defaultValue;
      if (input.type === "checkbox") input.checked = String(value) === "true";
      else input.value = value;
      input.addEventListener("change", () => {
        this.readIndicatorParameters();
        this.previewEditorIndicator();
      });
      label.append(input);
      parameterRoot.append(label);
    }
    this.syncEditorConditionOptions();
    const current = lastIndicatorValue(context.instance, context.outputSeries);
    fields.querySelector("[data-alert-indicator-preview]").textContent =
      Number.isFinite(current)
        ? `Chart value ${formatIndicatorValue(
            current,
            context.capability,
            context.outputSeries
          )} · ${this.localMode ? "local validation pending" : "server validation pending"}`
        : `${context.capability.displayName} is initializing ${
            this.localMode ? "in this chart" : "on the server"
          }.`;
  }

  renderIndicatorBEditorFields(context) {
    const fields = this.editorElement.querySelector("[data-alert-indicator-b-fields]");
    fields.hidden = !context;
    if (!context) return;
    const outputA = this.editorIndicator?.capability.outputs.find(
      (item) => item.id === this.editorIndicator.outputSeries
    );
    const capabilities = (this.indicatorCatalog?.indicators || [])
      .filter((item) =>
        item.isAvailable &&
        !item.isDeprecated &&
        item.outputs.some((output) =>
          !outputA || indicatorOutputsCompatible(outputA, output)));
    replaceSelectOptions(
      this.editorForm.elements.indicatorBId,
      capabilities.map((item) => [item.id, item.displayName])
    );
    const compatibleCapability = capabilities.find(
      (item) => item.id === context.capability.id
    ) || capabilities[0];
    if (compatibleCapability?.id !== context.capability.id) {
      this.editorIndicatorB = editorIndicatorFromCapability(compatibleCapability);
      context = this.editorIndicatorB;
    }
    this.editorForm.elements.indicatorBId.value = context.capability.id;
    const outputs = context.capability.outputs.filter(
      (output) => !outputA || indicatorOutputsCompatible(outputA, output)
    );
    replaceSelectOptions(
      this.editorForm.elements.indicatorBOutput,
      outputs.map((output) => [output.id, output.displayName])
    );
    context.outputSeries = outputs.some((output) => output.id === context.outputSeries)
      ? context.outputSeries
      : outputs[0]?.id || "";
    this.editorForm.elements.indicatorBOutput.value = context.outputSeries;
    const parameterRoot = fields.querySelector("[data-alert-indicator-b-parameters]");
    parameterRoot.replaceChildren();
    for (const parameter of context.capability.parameters) {
      const label = document.createElement("label");
      label.textContent = parameter.displayName;
      let input;
      if (parameter.options) {
        input = document.createElement("select");
        replaceSelectOptions(input, Object.entries(parameter.options));
      } else {
        input = document.createElement("input");
        input.type = parameter.type === "Boolean" ? "checkbox" : "number";
        if (parameter.minimum !== null) input.min = parameter.minimum;
        if (parameter.maximum !== null) input.max = parameter.maximum;
        if (parameter.step !== null) input.step = parameter.step;
      }
      input.dataset.indicatorBParameter = parameter.id;
      const value = context.parameters?.[parameter.id] ?? parameter.defaultValue;
      if (input.type === "checkbox") input.checked = String(value) === "true";
      else input.value = value;
      input.addEventListener("change", () => {
        this.readIndicatorBParameters();
        this.previewEditorIndicator();
      });
      label.append(input);
      parameterRoot.append(label);
    }
    const current = lastIndicatorValue(context.instance, context.outputSeries);
    fields.querySelector("[data-alert-indicator-b-preview]").textContent =
      Number.isFinite(current)
        ? `Chart value ${formatIndicatorValue(
            current,
            context.capability,
            context.outputSeries
          )} · ${this.localMode ? "local validation pending" : "server validation pending"}`
        : `${context.capability.displayName} is initializing ${
            this.localMode ? "in this chart" : "on the server"
          }.`;
  }

  readIndicatorParameters() {
    if (!this.editorIndicator) return {};
    const sourceIndicator = isIndicatorChoice(
      this.editorForm.elements.alertSource.value
    );
    this.editorIndicator.outputSeries = sourceIndicator
      ? this.editorForm.elements.sourceSeries.value
      : this.editorForm.elements.targetSeries.value;
    return this.editorIndicator.parameters;
  }

  readIndicatorBParameters() {
    if (!this.editorIndicatorB) return {};
    this.editorIndicatorB.outputSeries = this.editorForm.elements.targetSeries.value;
    return this.editorIndicatorB.parameters;
  }

  syncEditorConditionOptions(preferred = null) {
    const options = OPERATORS;
    const current = String(preferred || this.editorForm.elements.operator.value)
      .split(":")
      .at(-1);
    replaceSelectOptions(this.editorForm.elements.operator, options);
    this.editorForm.elements.operator.value = options.some(([value]) => value === current)
      ? current
      : options[0][0];
    syncChartDropdown(this.editorForm.elements.operator);
    this.syncEditorTargetControls();
  }

  syncEditorTargetControls() {
    const sourceIndicator = isIndicatorChoice(
      this.editorForm.elements.alertSource.value
    );
    const targetIndicator = isIndicatorChoice(
      this.editorForm.elements.targetType.value
    );
    const thresholdField = this.editorElement.querySelector("[data-alert-threshold-field]");
    const seriesField = this.editorElement.querySelector("[data-alert-target-series-field]");
    thresholdField.hidden = targetIndicator;
    seriesField.hidden = !targetIndicator;
    this.editorForm.elements.thresholdPrice.disabled = targetIndicator;
    this.editorForm.elements.targetSeries.disabled = !targetIndicator;
    this.editorForm.elements.thresholdPrice.min = sourceIndicator ? "" : "0.0000000001";
    if (targetIndicator) this.editorForm.elements.thresholdPrice.value = "0";
    this.editorElement.querySelector("[data-alert-price-help]").textContent =
      targetIndicator
        ? "Choose the indicator series used as the crossing target."
        : sourceIndicator
          ? "Indicator values are compared before display rounding."
          : "Price is snapped to the instrument tick size when saved.";
    this.refreshGeneratedName();
  }

  async previewEditorIndicator() {
    if (!this.editorIndicator) return;
    const generation = ++this.editorPreviewGeneration;
    this.readIndicatorParameters();
    if (this.editorIndicatorB) this.readIndicatorBParameters();
    this.syncEditorTargetControls();
    const preview = this.editorElement.querySelector("[data-alert-indicator-preview]");
    preview.textContent = this.localMode
      ? "Validating the chart indicator calculation\u2026"
      : "Validating canonical indicator calculation\u2026";
    try {
      const payload = this.editorPayload();
      const pair = payload.type === "IndicatorPair";
      const result = pair
        ? await this.repository.previewIndicatorPair({
            symbolId: payload.symbolId,
            operator: payload.operator,
            timeframe: payload.timeframe,
            triggerTiming: payload.triggerTiming,
            session: payload.session,
            indicatorA: unlinkIndicator(payload.indicator),
            indicatorB: unlinkIndicator(payload.indicatorB),
            sourceChartId: payload.sourceChartId
          })
        : await this.repository.previewIndicator({
            symbolId: payload.symbolId,
            type: payload.type,
            operator: payload.operator,
            thresholdPrice: payload.thresholdPrice,
            timeframe: payload.timeframe,
            triggerTiming: payload.triggerTiming,
            session: payload.session,
            indicator: unlinkIndicator(payload.indicator),
            sourceChartId: payload.sourceChartId
          });
      if (generation !== this.editorPreviewGeneration) return;
      const errors = Object.values(result.validationErrors || {}).flat();
      preview.textContent = errors.length
        ? errors.join(" ")
        : `${result.conditionDescription} · ${result.readiness}`
          + (result.currentIndicatorValue === null
            ? ""
            : ` · current ${result.currentIndicatorValue}`);
      if (!errors.length && pair) {
        preview.textContent = `${result.conditionDescription} · ${result.readiness}`
          + (result.currentIndicatorAValue === null
            ? ""
            : ` · A ${result.currentIndicatorAValue}`)
          + (result.currentIndicatorBValue === null
            ? ""
            : ` · B ${result.currentIndicatorBValue}`)
          + (result.currentDifference === null ||
             result.currentDifference === undefined
            ? ""
            : ` · difference ${result.currentDifference}`)
          + (result.sharedWatermark
            ? ` · matched ${result.sharedWatermark}`
            : "");
      }
      preview.dataset.state = errors.length ? "error" : String(result.readiness).toLowerCase();
    } catch (error) {
      if (generation !== this.editorPreviewGeneration) return;
      preview.textContent = error.message;
      preview.dataset.state = "error";
    }
  }

  refreshGeneratedName() {
    if (this.editorNameEdited) return;
    const form = this.editorForm;
    const price = Number(form.elements.thresholdPrice.value);
    if (!Number.isFinite(price)) return;
    const sourceIndicator = isIndicatorChoice(form.elements.alertSource.value);
    const targetIndicator = isIndicatorChoice(form.elements.targetType.value);
    if (sourceIndicator && targetIndicator && this.editorIndicatorB) {
      form.elements.name.value = `${this.editorIndicator.capability.displayName} ${
        operatorLabel(form.elements.operator.value).toLowerCase()
      } ${this.editorIndicatorB.capability.displayName}`;
    } else if (sourceIndicator && this.editorIndicator) {
      form.elements.name.value = generatedIndicatorName(
        form.dataset.symbol,
        this.editorIndicator.capability.displayName,
        form.elements.operator.value,
        price
      );
    } else if (targetIndicator && this.editorIndicator) {
      form.elements.name.value = generatedIndicatorName(
        form.dataset.symbol,
        this.editorIndicator.capability.displayName,
        form.elements.operator.value,
        price,
        true
      );
    } else {
      form.elements.name.value = generatedName(
        form.dataset.symbol,
        form.elements.operator.value,
        price
      );
    }
  }

  async submitEditor(event) {
    event.preventDefault();
    const submit = this.editorElement.querySelector("[data-alert-submit]");
    if (submit.disabled) return;
    this.clearEditorError();
    const payload = this.editorPayload();
    if (!this.editorAlert) {
      const disabledReason = this.alertCreationDisabledReason();
      if (disabledReason) {
        this.showEditorError(disabledReason);
        this.syncPolicyControls();
        return;
      }
    }
    const clientError = validateEditorPayload(payload);
    if (clientError) {
      this.showEditorError(clientError);
      return;
    }
    submit.disabled = true;
    submit.textContent = this.editorAlert ? "Saving\u2026" : "Creating\u2026";
    try {
      if (payload.type === "HorizontalDrawing" && this.editorDrawing) {
        const price = Number(this.editorDrawing.anchors?.[0]?.price ?? payload.thresholdPrice);
        const resource = await this.repository.upsertDrawing(
          payload.sourceChartId,
          payload.sourceDrawingId,
          price,
          this.drawingVersions.get(payload.sourceDrawingId) ?? null
        );
        this.drawingVersions.set(payload.sourceDrawingId, resource.version);
      }
      if (
        ["IndicatorValue", "PriceIndicator", "IndicatorPair"].includes(payload.type) &&
        this.editorIndicator?.resourceId &&
        payload.sourceChartId
      ) {
        const resource = await this.repository.upsertIndicator(
          payload.sourceChartId,
          this.editorIndicator.resourceId,
          payload.indicator,
          this.editorIndicator.resourceVersion
        );
        this.indicatorVersions.set(this.editorIndicator.resourceId, resource.version);
        this.editorIndicator.resourceVersion = resource.version;
        payload.indicator.sourceIndicatorId = this.editorIndicator.resourceId;
        payload.indicator.sourceIndicatorVersion = resource.version;
      }
      if (
        payload.type === "IndicatorPair" &&
        this.editorIndicatorB?.resourceId &&
        payload.sourceChartId
      ) {
        const resource = await this.repository.upsertIndicator(
          payload.sourceChartId,
          this.editorIndicatorB.resourceId,
          payload.indicatorB,
          this.editorIndicatorB.resourceVersion
        );
        this.indicatorVersions.set(this.editorIndicatorB.resourceId, resource.version);
        this.editorIndicatorB.resourceVersion = resource.version;
        payload.indicatorB.sourceIndicatorId = this.editorIndicatorB.resourceId;
        payload.indicatorB.sourceIndicatorVersion = resource.version;
      }
      if (this.editorAlert) {
        await this.repository.update(this.editorAlert, payload);
      } else {
        await this.repository.create(payload);
      }
      await this.refresh({ quiet: true });
      this.closeEditor();
      this.showToast(this.editorAlert ? "Alert updated." : "Alert created.");
    } catch (error) {
      if (isProductPolicyError(error)) await this.refresh({ quiet: true });
      this.showEditorError(alertErrorMessage(error), error.validationErrors);
    } finally {
      submit.disabled = false;
      submit.textContent = this.editorAlert ? "Save changes" : "Create alert";
      this.syncPolicyControls();
    }
  }

  editorPayload() {
    const form = this.editorForm;
    const sourceIndicator = isIndicatorChoice(form.elements.alertSource.value);
    const targetIndicator = isIndicatorChoice(form.elements.targetType.value);
    const isIndicator = sourceIndicator || targetIndicator;
    const isPair = sourceIndicator && targetIndicator;
    if (isIndicator) this.readIndicatorParameters();
    if (isPair) this.readIndicatorBParameters();
    const indicatorType = isPair
      ? "IndicatorPair"
      : sourceIndicator
        ? "IndicatorValue"
        : targetIndicator
          ? "PriceIndicator"
          : null;
    return {
      name: form.elements.name.value.trim(),
      customMessage: form.elements.customMessage.value.trim() || null,
      symbolId: form.dataset.symbolId,
      type: isIndicator
        ? indicatorType
        : form.dataset.type === "HorizontalDrawing"
          ? "HorizontalDrawing"
          : "FixedPrice",
      operator: form.elements.operator.value,
      thresholdPrice: ["PriceIndicator", "IndicatorPair"].includes(indicatorType)
        ? 0
        : Number(form.elements.thresholdPrice.value),
      timeframe: form.elements.triggerTiming.value === "CompletedBar" || isIndicator
        ? form.elements.timeframe.value
        : null,
      triggerTiming: form.elements.triggerTiming.value,
      frequency: form.elements.frequency.value,
      session: form.elements.session.value,
      expiresAtUtc: form.elements.expiration.value
        ? new Date(form.elements.expiration.value).toISOString()
        : null,
      notificationChannels: "InApp",
      sourceChartId: form.dataset.sourceChartId || null,
      sourceDrawingId: isIndicator ? null : form.dataset.sourceDrawingId || null,
      indicator: isIndicator
        ? {
            schemaVersion: 1,
            indicatorId: this.editorIndicator.capability.id,
            algorithmVersion: this.editorIndicator.capability.algorithmVersion,
            parameters: this.editorIndicator.parameters,
            inputSeries: this.editorIndicator.inputSeries,
            outputSeries: this.editorIndicator.outputSeries,
            adjustmentMode: this.editorIndicator.adjustmentMode || "Unadjusted",
            sourceIndicatorId: this.editorIndicator.resourceId || null,
            sourceIndicatorVersion: this.editorIndicator.resourceVersion || null
          }
        : null,
      indicatorB: isPair
        ? {
            schemaVersion: 1,
            indicatorId: this.editorIndicatorB.capability.id,
            algorithmVersion: this.editorIndicatorB.capability.algorithmVersion,
            parameters: this.editorIndicatorB.parameters,
            inputSeries: this.editorIndicatorB.inputSeries,
            outputSeries: this.editorIndicatorB.outputSeries,
            adjustmentMode: this.editorIndicatorB.adjustmentMode || "Unadjusted",
            sourceIndicatorId: this.editorIndicatorB.resourceId || null,
            sourceIndicatorVersion: this.editorIndicatorB.resourceVersion || null
          }
        : null
    };
  }

  renderManager() {
    this.syncPolicyControls();
    if (!this.managerElement || this.managerElement.hidden) return;
    const isLog = this.managerTab === "Log";
    const sortButton = this.managerElement.querySelector("[data-alert-sort-button]");
    const sortMenu = this.managerElement.querySelector("[data-alert-sort-menu]");
    sortButton.setAttribute("aria-expanded", String(this.managerSortOpen));
    sortMenu.hidden = !this.managerSortOpen;
    for (const option of sortMenu.querySelectorAll("[data-alert-sort-value]")) {
      const selected = option.dataset.alertSortValue === this.managerSort;
      option.setAttribute("aria-selected", String(selected));
    }
    for (const button of this.managerElement.querySelectorAll("[data-alert-tab]")) {
      const selected = button.dataset.alertTab === this.managerTab;
      button.setAttribute("aria-selected", String(selected));
      button.tabIndex = selected ? 0 : -1;
    }
    const values = this.managerValues();
    this.managerList.replaceChildren();
    if (values.length === 0) {
      const empty = document.createElement("div");
      empty.className = "alert-empty-state";
      empty.innerHTML = `<strong>${this.managerSearch ? "No matches" : `No ${this.managerTab.toLowerCase()} alerts`}</strong>
        <p>${isLog
          ? "Trigger history will remain here even after an alert is deleted."
          : this.localMode
            ? "Create a local alert. It stays active while FlexCharting is open."
            : "Create a price, drawing, or indicator alert from a chart."}</p>`;
      this.managerList.append(empty);
    } else {
      for (const item of values) {
        this.managerList.append(
          isLog
            ? this.buildTriggerRow(item)
            : this.buildAlertRow(item)
        );
      }
    }
    this.setManagerState("", "");
    this.syncBulkBar();
  }

  managerValues() {
    const search = this.managerSearch.trim().toLowerCase();
    const isLog = this.managerTab === "Log";
    let values = isLog
      ? [...this.triggers]
      : [...this.alerts];
    if (search) {
      values = values.filter((item) =>
        [
          item.symbol,
          item.name,
          item.alertName,
          item.renderedMessage,
          item.indicatorId,
          item.indicatorBId,
          item.indicatorOutputDisplayName,
          item.indicatorBOutputDisplayName,
          item.calculationReadiness,
          item.pairReadiness
        ]
          .some((value) => String(value || "").toLowerCase().includes(search)));
    }
    values.sort((a, b) => {
      if (this.managerSort === "symbol") return a.symbol.localeCompare(b.symbol);
      if (this.managerSort === "threshold") {
        const left = isLog ? a.observedPrice : a.thresholdPrice;
        const right = isLog ? b.observedPrice : b.thresholdPrice;
        return Number(left) - Number(right);
      }
      if (isLog || this.managerSort === "triggered") {
        const left = isLog ? a.triggeredAtUtc : a.lastTriggeredAtUtc;
        const right = isLog ? b.triggeredAtUtc : b.lastTriggeredAtUtc;
        return Date.parse(right || 0) - Date.parse(left || 0);
      }
      return Date.parse(b.updatedAtUtc) - Date.parse(a.updatedAtUtc);
    });
    return values;
  }

  buildAlertRow(alert) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = `alert-manager-row status-${alert.status.toLowerCase()}`;
    const quotaSuspended = this.localQuotaSuspendedIds.has(alert.id);
    row.classList.toggle("is-quota-suspended", quotaSuspended);
    row.dataset.alertId = alert.id;
    row.dataset.alertRowAction = "edit";
    const condition = compactConditionText(alertConditionLabel(alert));
    row.setAttribute(
      "aria-label",
      `Edit ${alert.symbol}: ${condition}${quotaSuspended ? ". Quota suspended" : ""}`
    );
    if (quotaSuspended) {
      row.title = "This alert is retained but is not evaluated because it exceeds the current product limit.";
    }
    row.innerHTML = `
      <strong class="alert-row-symbol">${escapeHtml(alert.symbol)}</strong>
      <span class="alert-row-condition">${escapeHtml(condition)}</span>
    `;
    return row;
  }

  buildTriggerRow(trigger) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = `alert-manager-row alert-trigger-row${trigger.isRead ? "" : " is-unread"}`;
    row.dataset.triggerId = trigger.id;
    row.dataset.triggerRowAction = "open";
    const condition = compactConditionText(
      trigger.conditionSnapshot || trigger.alertName || trigger.renderedMessage
    );
    row.setAttribute("aria-label", `Open ${trigger.symbol}: ${condition}`);
    row.innerHTML = `
      <strong class="alert-row-symbol">${escapeHtml(trigger.symbol)}</strong>
      <span class="alert-row-condition">${escapeHtml(condition)}</span>
    `;
    return row;
  }

  async handleManagerClick(event) {
    const alertRow = event.target.closest("[data-alert-id]");
    const alertAction = event.target.closest("[data-alert-row-action]")?.dataset.alertRowAction;
    if (alertRow && alertAction) {
      const alert = this.alerts.find((item) => item.id === alertRow.dataset.alertId);
      if (!alert) return;
      if (alertAction === "edit") {
        this.openEditor({
          instance: this.chartForAlert(alert) || this.getActiveChart(),
          alert
        });
        return;
      }
      if (alertAction === "open") {
        this.openRelatedChart(alert);
        return;
      }
      await this.runAlertAction(alert, alertAction);
      return;
    }

    const triggerRow = event.target.closest("[data-trigger-id]");
    const triggerAction =
      event.target.closest("[data-trigger-row-action]")?.dataset.triggerRowAction;
    if (triggerRow && triggerAction) {
      const trigger = this.triggers.find((item) => item.id === triggerRow.dataset.triggerId);
      if (!trigger) return;
      if (triggerAction === "read") {
        await this.repository.markRead(trigger.id);
        await this.refresh({ quiet: true });
        await this.refreshUnreadCount({ notify: false });
      } else {
        this.openRelatedChart(trigger);
        if (!trigger.isRead) await this.repository.markRead(trigger.id);
      }
    }
  }

  handleManagerSelection(event) {
    const checkbox = event.target.closest("[data-alert-select]");
    const row = event.target.closest("[data-alert-id]");
    if (!checkbox || !row) return;
    if (checkbox.checked) this.selectedIds.add(row.dataset.alertId);
    else this.selectedIds.delete(row.dataset.alertId);
    this.syncBulkBar();
  }

  async handleBulkAction(event) {
    const button = event.target.closest("[data-alert-bulk-action]");
    if (!button || this.selectedIds.size === 0) return;
    const action = button.dataset.alertBulkAction;
    if (["resume", "rearm"].includes(action)) {
      const selected = this.alerts.filter((alert) => this.selectedIds.has(alert.id));
      const additional = selected.filter((alert) => alert.status !== "Active").length;
      const disabledReason = this.alertCreationDisabledReason(additional);
      if (disabledReason) {
        this.showToast(disabledReason, { kind: "error" });
        return;
      }
    }
    if (action === "delete" && !window.confirm(
      `Delete ${this.selectedIds.size} selected alert definitions? Trigger history will be preserved.`
    )) return;
    button.disabled = true;
    try {
      await this.repository.bulk(action, [...this.selectedIds]);
      this.selectedIds.clear();
      await this.refresh({ quiet: true });
      this.showToast(`Bulk ${action} completed.`);
    } catch (error) {
      if (isProductPolicyError(error)) await this.refresh({ quiet: true });
      this.showToast(alertErrorMessage(error), { kind: "error" });
    } finally {
      button.disabled = false;
    }
  }

  async runAlertAction(alert, action) {
    if (action === "delete") {
      if (!window.confirm(
        `Delete "${alert.name}"? Its trigger history will be preserved.`
      )) return false;
    }
    if (["duplicate", "resume", "rearm"].includes(action)) {
      const disabledReason = this.alertCreationDisabledReason(
        alertActionAdditionalSlots(alert, action)
      );
      if (disabledReason) {
        this.showToast(disabledReason, { kind: "error" });
        return false;
      }
    }
    try {
      if (action === "delete") await this.repository.remove(alert);
      else await this.repository.action(alert, action);
      await this.refresh({ quiet: true });
      this.showToast(`Alert ${action === "delete" ? "deleted" : `${action}d`}.`);
      return true;
    } catch (error) {
      if (isProductPolicyError(error)) await this.refresh({ quiet: true });
      this.showToast(alertErrorMessage(error), { kind: "error" });
      return false;
    }
  }

  activeAlertCount() {
    return this.alerts.filter((alert) => alert.status === "Active").length;
  }

  alertCreationDisabledReason(additional = 1) {
    if (!this.canCreateAlerts || this.maximumActiveAlerts === 0) {
      return "This product does not include alerts.";
    }
    if (
      Number.isFinite(this.maximumActiveAlerts)
      && this.activeAlertCount() + Math.max(0, Number(additional) || 0)
        > this.maximumActiveAlerts
    ) {
      return "You have reached the active alert limit for this product.";
    }
    return null;
  }

  syncPolicyControls() {
    const reason = this.alertCreationDisabledReason();
    const create = this.managerElement?.querySelector("[data-alert-create]");
    if (create instanceof HTMLButtonElement) {
      create.disabled = Boolean(reason);
      create.title = reason || "New alert";
      create.setAttribute("aria-label", reason ? `New alert unavailable. ${reason}` : "New alert");
    }
    const submit = this.editorElement?.querySelector("[data-alert-submit]");
    if (submit instanceof HTMLButtonElement && !this.editorAlert) {
      submit.disabled = Boolean(reason);
      submit.title = reason || "Create alert";
    }
    this.root.dataset.activeAlertCount = String(this.activeAlertCount());
    this.root.dataset.activeAlertLimit = Number.isFinite(this.maximumActiveAlerts)
      ? String(this.maximumActiveAlerts)
      : "unlimited";
  }

  async handleContextAction(event) {
    const button = event.target.closest("[data-alert-context-action]");
    if (!button || !this.contextAlert) return;
    const action = button.dataset.alertContextAction;
    const alert = this.contextAlert;
    const instance = this.contextInstance;
    this.closeAlertContextMenu();
    if (action === "edit") {
      this.openEditor({ instance, alert });
      return;
    }
    if (action === "manager") {
      this.openManager(alert.status === "Triggered" ? "Log" : "Active");
      return;
    }
    await this.runAlertAction(alert, action);
  }

  beginLineDrag(instance, event) {
    if (event.button !== 0 || this.drag) return;
    const alert = this.alertAtPoint(instance, event, { plotOnly: true });
    if (
      !alert ||
      !["FixedPrice", "IndicatorValue"].includes(alert.type) ||
      alert.status === "Triggered"
    ) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    instance.overlayCanvas.setPointerCapture?.(event.pointerId);
    this.drag = {
      instance,
      alert,
      pointerId: event.pointerId,
      originalPrice: Number(alert.thresholdPrice),
      proposedPrice: Number(alert.thresholdPrice)
    };
    instance.overlayCanvas.classList.add("is-dragging-alert");
  }

  updateLineDrag(instance, event) {
    if (!this.drag || this.drag.pointerId !== event.pointerId || this.drag.instance !== instance) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const point = localPoint(instance.overlayCanvas, event);
    const proposed = this.drag.alert.type === "IndicatorValue"
      ? indicatorAlertValueAtY(instance, this.drag.alert, point.y)
      : instance.workspace.renderInfo?.yToPrice?.(point.y);
    if (!Number.isFinite(proposed)) return;
    const snapped = this.drag.alert.type === "IndicatorValue" &&
      this.drag.alert.indicatorOutputUnit !== "Price"
      ? Number(proposed.toFixed(8))
      : snapPrice(
          proposed,
          this.drag.alert.tickSize,
          this.drag.alert.pricePrecision
        );
    this.drag.proposedPrice = snapped;
    this.drag.alert.thresholdPrice = snapped;
    this.syncCharts();
    this.showToast(`Alert price ${formatPrice(snapped, this.drag.alert.pricePrecision)}`, {
      sticky: true
    });
  }

  async commitLineDrag(instance, event) {
    if (!this.drag || this.drag.pointerId !== event.pointerId || this.drag.instance !== instance) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const drag = this.drag;
    this.drag = null;
    instance.overlayCanvas.classList.remove("is-dragging-alert");
    this.hideToast();
    if (drag.proposedPrice === drag.originalPrice) return;
    try {
      await this.repository.update(
        drag.alert,
        updatePayload(drag.alert, { thresholdPrice: drag.proposedPrice })
      );
      await this.refresh({ quiet: true });
      this.showToast("Alert price updated.");
    } catch (error) {
      drag.alert.thresholdPrice = drag.originalPrice;
      this.syncCharts();
      this.showToast(`Alert price was restored: ${error.message}`, { kind: "error" });
    }
  }

  cancelLineDrag(instance, event) {
    if (!this.drag || this.drag.pointerId !== event.pointerId || this.drag.instance !== instance) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.drag.alert.thresholdPrice = this.drag.originalPrice;
    this.drag = null;
    instance.overlayCanvas.classList.remove("is-dragging-alert");
    this.syncCharts();
    this.hideToast();
  }

  alertAtPoint(instance, event, { plotOnly = false } = {}) {
    const info = instance?.workspace?.renderInfo;
    if (!info?.layout) return null;
    const point = localPoint(instance.overlayCanvas, event);
    const { layout } = info;
    const withinX = plotOnly
      ? point.x >= layout.plotLeft && point.x <= layout.plotRight
      : point.x >= layout.plotLeft && point.x <= layout.plotRight + layout.axisWidth;
    if (!withinX) return null;
    const symbolId = String(instance.symbolId || "").toLowerCase();
    return this.alerts
      .filter((alert) =>
        String(alert.symbolId || "").toLowerCase() === symbolId &&
        ["Active", "Paused"].includes(alert.status))
      .sort((a, b) => Number(b.updatedAtUtc) - Number(a.updatedAtUtc))
      .find((alert) => {
        const geometry = alertGeometry(instance, alert);
        return geometry &&
          point.y >= geometry.top &&
          point.y <= geometry.bottom &&
          Math.abs(geometry.y - point.y) <= 7;
      }) || null;
  }

  async refreshUnreadCount({ notify }) {
    try {
      const result = await this.repository.unreadCount();
      const count = Number(result?.count || 0);
      const badge = this.openButton?.querySelector("[data-alert-unread-badge]");
      if (badge) {
        badge.textContent = count > 99 ? "99+" : String(count);
        badge.hidden = count === 0;
      }
      this.openButton?.setAttribute(
        "aria-label",
        count === 0 ? "Open Alert Manager" : `Open Alert Manager, ${count} unread alerts`
      );
      if (
        notify &&
        this.unreadInitialized &&
        count > this.lastUnreadCount
      ) {
        const unread = await this.repository.history({ isRead: false });
        const latest = unread[0];
        if (latest && rememberTriggerForToast(this.seenTriggerIds, latest.id)) {
          this.showTriggerToast(latest);
        }
      }
      this.lastUnreadCount = count;
      this.unreadInitialized = true;
    } catch {
      this.openButton?.classList.add("is-service-unavailable");
    }
  }

  showTriggerToast(trigger) {
    this.toast.replaceChildren();
    this.toast.className = "alert-toast is-trigger";
    const content = document.createElement("button");
    content.type = "button";
    content.innerHTML = `<strong>${escapeHtml(trigger.alertName)}</strong><span>${escapeHtml(trigger.renderedMessage)}</span>`;
    content.addEventListener("click", async () => {
      this.openRelatedChart(trigger);
      await this.repository.markRead(trigger.id);
      this.hideToast();
      await this.refresh({ quiet: true });
      await this.refreshUnreadCount({ notify: false });
    });
    const close = document.createElement("button");
    close.type = "button";
    close.className = "alert-toast-close";
    close.setAttribute("aria-label", "Dismiss alert notification");
    close.textContent = "\u00d7";
    close.addEventListener("click", () => this.hideToast());
    this.toast.append(content, close);
    this.toast.hidden = false;
  }

  showBrowserNotification(trigger) {
    if (!this.localMode || !("Notification" in window) || Notification.permission !== "granted") {
      return;
    }
    const notification = new Notification(trigger.alertName, {
      body: trigger.renderedMessage,
      tag: trigger.occurrenceKey,
      renotify: false
    });
    notification.addEventListener("click", () => {
      window.focus();
      this.openRelatedChart(trigger);
      notification.close();
    });
  }

  showToast(message, { kind = "success", sticky = false } = {}) {
    this.toast.replaceChildren();
    this.toast.className = `alert-toast is-${kind}`;
    this.toast.textContent = message;
    this.toast.hidden = false;
    window.clearTimeout(this.toastTimer);
    if (!sticky) {
      this.toastTimer = window.setTimeout(() => this.hideToast(), 4_500);
    }
    this.announce(message);
  }

  hideToast() {
    window.clearTimeout(this.toastTimer);
    this.toast.hidden = true;
  }

  openRelatedChart(item) {
    const chart = this.chartForAlert(item);
    if (chart) {
      this.focusChart?.(chart);
      if (chart.floatingWindow?.state === "minimized") chart.floatingWindow.restore();
      chart.overlayCanvas?.focus({ preventScroll: true });
      this.showToast(`${item.symbol} chart focused.`);
      return;
    }
    this.showToast(
      `Open a chart for ${item.symbol} to inspect this alert.`,
      { kind: "error" }
    );
  }

  chartForAlert(item) {
    const symbolId = String(item.symbolId || "").toLowerCase();
    return this.getCharts().find(
      (chart) => String(chart.symbolId || "").toLowerCase() === symbolId
    ) || null;
  }

  closeManager() {
    this.managerSortOpen = false;
    this.managerElement.hidden = true;
    this.managerElement.classList.remove("is-focused");
    this.openButton?.focus({ preventScroll: true });
    this.emitObjectTreeChange();
    this.onWindowStateChange();
  }

  closeEditor() {
    this.editorElement.hidden = true;
    this.editorElement.classList.remove("is-focused");
    this.editorAlert = null;
    this.editorDrawing = null;
    this.editorIndicator = null;
    this.editorIndicatorB = null;
    if (this.editorInstance) {
      this.focusChart?.(this.editorInstance);
      this.editorInstance.overlayCanvas?.focus({ preventScroll: true });
    }
    this.editorInstance = null;
    this.emitObjectTreeChange();
  }

  setFocusedAlertWindow(windowElement = null) {
    let changed = false;
    if (windowElement) {
      this.onFocusWindow();
      if (windowElement === this.editorElement) {
        windowElement.style.removeProperty("z-index");
      } else {
        const stackOrder = Number(this.nextStackOrder?.());
        if (Number.isFinite(stackOrder)) {
          changed ||= this.managerStackOrder !== stackOrder;
          this.managerStackOrder = stackOrder;
          windowElement.style.zIndex = String(stackOrder);
        }
      }
    }
    for (const candidate of [this.managerElement, this.editorElement]) {
      const focused = candidate === windowElement && !candidate.hidden;
      changed ||= candidate?.classList.contains("is-focused") !== focused;
      candidate?.classList.toggle("is-focused", focused);
    }
    // Losing alert-window focus commonly happens during pointerdown on another
    // Object Tree action. Re-rendering the tree at that point replaces the
    // pressed control before its click event can run. The destination action
    // already publishes the next workspace snapshot; publish immediately only
    // when an alert window is being raised.
    if (changed && windowElement) this.emitObjectTreeChange();
    if (changed && windowElement === this.managerElement) this.onWindowStateChange();
  }

  snapshotManagerState() {
    return {
      open: Boolean(this.managerElement && !this.managerElement.hidden),
      geometry: this.managerFloating?.snapshot() || null,
      stackOrder: this.managerStackOrder,
      tab: this.managerTab,
      sort: this.managerSort
    };
  }

  restoreManagerState(state) {
    if (!state || typeof state !== "object" || !this.managerElement) return;
    const geometry = state.geometry;
    if (
      geometry
      && [geometry.x, geometry.y, geometry.width, geometry.height]
        .every(Number.isFinite)
      && geometry.width >= 160
      && geometry.height >= 120
    ) {
      this.managerFloating.applyGeometry(geometry);
      this.managerHasOpened = true;
    }
    if (state.tab === "Active" || state.tab === "Log") this.managerTab = state.tab;
    if (["updated", "symbol", "threshold", "triggered"].includes(state.sort)) {
      this.managerSort = state.sort;
    }
    if (Number.isFinite(state.stackOrder) && state.stackOrder > 0) {
      this.managerStackOrder = state.stackOrder;
      this.managerElement.style.zIndex = String(state.stackOrder);
    }
    this.managerElement.hidden = state.open !== true;
    this.managerElement.classList.toggle("is-focused", state.open === true);
    this.renderManager();
    if (state.open === true) this.managerFloating.constrainToSurface();
  }

  subscribeObjectTree(listener) {
    if (typeof listener !== "function") return () => {};
    this.objectTreeListeners.add(listener);
    return () => this.objectTreeListeners.delete(listener);
  }

  emitObjectTreeChange() {
    this.onWorkspaceChange();
    for (const listener of [...this.objectTreeListeners]) listener();
  }

  objectTreeManager() {
    if (!this.managerElement || this.managerElement.hidden) return null;
    return {
      id: "alert-manager",
      label: "Alert Manager",
      description: `${this.alerts.length} alerts`,
      stackOrder: this.managerStackOrder,
      active: this.managerElement.classList.contains("is-focused")
    };
  }

  objectTreeAlertsForChart(chart) {
    if (!chart) return [];
    return this.alerts.filter((alert) => this.objectTreeChartForAlert(alert) === chart)
      .map((alert) => ({
        id: alert.id,
        label: alertConditionLabel(alert),
        description: alert.name || "",
        status: alert.status || "",
        paused: alert.status === "Paused"
      }));
  }

  objectTreeChartForAlert(alert) {
    const charts = this.getCharts();
    if (alert.sourceDrawingId) {
      const drawingOwners = charts.filter((chart) =>
        chart.drawings?.some((drawing) => drawing.id === alert.sourceDrawingId)
      );
      if (drawingOwners.length === 1) return drawingOwners[0];
      if (drawingOwners.length > 1) return null;
    }
    if (alert.sourceIndicatorId) {
      const indicatorOwners = charts.filter((chart) =>
        chart.indicatorInstances?.some(
          (indicator) => indicator.id === alert.sourceIndicatorId
        )
      );
      if (indicatorOwners.length === 1) return indicatorOwners[0];
      if (indicatorOwners.length > 1) return null;
    }
    const symbolId = String(alert.symbolId || "").toLowerCase();
    const symbolOwners = charts.filter(
      (chart) => String(chart.symbolId || "").toLowerCase() === symbolId
    );
    return symbolOwners.length === 1 ? symbolOwners[0] : null;
  }

  activateAlertById(alertId) {
    const alert = this.alerts.find((item) => item.id === alertId);
    if (!alert) return false;
    this.openRelatedChart(alert);
    return true;
  }

  editAlertById(alertId) {
    const alert = this.alerts.find((item) => item.id === alertId);
    if (!alert) return false;
    this.openEditor({
      instance: this.chartForAlert(alert) || this.getActiveChart(),
      alert
    });
    return true;
  }

  async deleteAlertById(alertId) {
    const alert = this.alerts.find((item) => item.id === alertId);
    if (!alert) return false;
    return this.runAlertAction(alert, "delete");
  }

  syncAlertWindowFocus(target) {
    if (!(target instanceof Node)) return;
    const focusedWindow = [this.editorElement, this.managerElement]
      .find((candidate) => !candidate.hidden && candidate.contains(target));
    this.setFocusedAlertWindow(focusedWindow || null);
  }

  closeAlertContextMenu(restoreFocus = false) {
    this.contextMenu.hidden = true;
    if (restoreFocus) this.contextInstance?.overlayCanvas?.focus({ preventScroll: true });
    this.contextAlert = null;
    this.contextInstance = null;
  }

  closeDrawingDeleteDialog() {
    this.deleteDialog.hidden = true;
    this.pendingDrawingDelete?.instance?.overlayCanvas?.focus({ preventScroll: true });
    this.pendingDrawingDelete = null;
  }

  async deleteDrawingAndAlert() {
    const pending = this.pendingDrawingDelete;
    if (!pending) return;
    try {
      await this.repository.remove(pending.alert);
      await this.deleteAuthoritativeDrawing(pending);
      pending.deleteDrawing();
      this.closeDrawingDeleteDialog();
      await this.refresh({ quiet: true });
      this.showToast("Drawing and alert deleted.");
    } catch (error) {
      this.showToast(error.message, { kind: "error" });
    }
  }

  async preserveAlertAndDeleteDrawing() {
    const pending = this.pendingDrawingDelete;
    if (!pending) return;
    try {
      await this.repository.update(
        pending.alert,
        updatePayload(pending.alert, {
          type: "FixedPrice",
          sourceChartId: null,
          sourceDrawingId: null
        })
      );
      await this.deleteAuthoritativeDrawing(pending);
      pending.deleteDrawing();
      this.closeDrawingDeleteDialog();
      await this.refresh({ quiet: true });
      this.showToast("Drawing deleted; fixed-price alert preserved.");
    } catch (error) {
      this.showToast(error.message, { kind: "error" });
    }
  }

  async deleteAuthoritativeDrawing(pending) {
    const drawingId = pending.drawing.id;
    let version = this.drawingVersions.get(drawingId);
    if (version == null) {
      const price = Number(pending.drawing.anchors?.[0]?.price);
      const resource = await this.repository.upsertDrawing(
        pending.alert.sourceChartId,
        drawingId,
        price,
        null
      );
      version = resource.version;
    }
    await this.repository.deleteDrawing(
      pending.alert.sourceChartId,
      drawingId,
      version
    );
    this.drawingVersions.delete(drawingId);
  }

  showEditorError(message, errors = null) {
    const error = this.editorElement.querySelector("[data-alert-form-error]");
    const details = errors
      ? Object.values(errors).flat().join(" ")
      : "";
    error.textContent = [message, details].filter(Boolean).join(" ");
    error.hidden = false;
    error.focus?.();
  }

  clearEditorError() {
    const error = this.editorElement.querySelector("[data-alert-form-error]");
    error.textContent = "";
    error.hidden = true;
  }

  setManagerState(kind, message) {
    const state = this.managerElement.querySelector("[data-alert-manager-state]");
    state.dataset.state = kind;
    state.textContent = message;
    state.hidden = !message;
  }

  syncBulkBar() {
    const bar = this.managerElement.querySelector("[data-alert-bulk-bar]");
    bar.hidden = this.selectedIds.size === 0 || this.managerTab === "Log";
    bar.querySelector("[data-alert-selected-count]").textContent =
      `${this.selectedIds.size} selected`;
  }
}

function updatePayload(alert, patch = {}) {
  return {
    name: alert.name,
    customMessage: alert.customMessage,
    type: alert.type,
    operator: alert.operator,
    thresholdPrice: Number(alert.thresholdPrice),
    timeframe: alert.timeframe,
    triggerTiming: alert.triggerTiming,
    frequency: alert.frequency,
    session: alert.session,
    expiresAtUtc: alert.expiresAtUtc,
    notificationChannels: alert.notificationChannels,
    sourceChartId: alert.sourceChartId,
    sourceDrawingId: alert.sourceDrawingId,
    indicator: alert.indicatorId
      ? {
          schemaVersion: 1,
          indicatorId: alert.indicatorId,
          algorithmVersion: alert.indicatorAlgorithmVersion,
          parameters: parseParameters(alert.indicatorParametersJson),
          inputSeries: alert.indicatorInputSeries,
          outputSeries: alert.indicatorOutputSeries,
          adjustmentMode: alert.adjustmentMode || "Unadjusted",
          sourceIndicatorId: alert.sourceIndicatorId,
          sourceIndicatorVersion: alert.sourceIndicatorVersion
        }
      : null,
    indicatorB: alert.indicatorBId
      ? {
          schemaVersion: 1,
          indicatorId: alert.indicatorBId,
          algorithmVersion: alert.indicatorBAlgorithmVersion,
          parameters: parseParameters(alert.indicatorBParametersJson),
          inputSeries: alert.indicatorBInputSeries,
          outputSeries: alert.indicatorBOutputSeries,
          adjustmentMode: alert.adjustmentMode || "Unadjusted",
          sourceIndicatorId: alert.sourceIndicatorBId,
          sourceIndicatorVersion: alert.sourceIndicatorBVersion
        }
      : null,
    ...patch
  };
}

function validateEditorPayload(payload) {
  if (!payload.name) return "Alert name is required.";
  const indicator = ["IndicatorValue", "PriceIndicator", "IndicatorPair"].includes(
    payload.type
  );
  if (!Number.isFinite(payload.thresholdPrice) || (!indicator && payload.thresholdPrice <= 0)) {
    return "Enter a valid positive price.";
  }
  if (indicator && !payload.indicator) return "Choose a supported indicator.";
  if (payload.type === "IndicatorPair" && !payload.indicatorB) {
    return "Choose a second compatible indicator.";
  }
  if (
    payload.type === "IndicatorPair" &&
    !payload.indicatorB?.outputSeries
  ) {
    return "Choose a compatible Indicator B output.";
  }
  if (
    ["PriceIndicator", "IndicatorPair"].includes(payload.type) &&
    payload.thresholdPrice !== 0
  ) {
    return "This crossing condition does not use a fixed threshold.";
  }
  if (!payload.notificationChannels) return "Select at least one notification method.";
  if (payload.expiresAtUtc && Date.parse(payload.expiresAtUtc) <= Date.now()) {
    return "Expiration must be in the future.";
  }
  if (
    payload.type === "HorizontalDrawing" &&
    (!payload.sourceChartId || !payload.sourceDrawingId)
  ) return "The horizontal line is no longer available.";
  return "";
}

function generatedName(symbol, operator, price) {
  return `${symbol} ${operatorLabel(operator).toLowerCase()} ${formatPrice(price, 2)}`;
}

function generatedIndicatorName(
  symbol,
  indicator,
  operator,
  threshold,
  priceComparison = false
) {
  return priceComparison
    ? `${symbol} price ${operatorLabel(operator).toLowerCase()} ${indicator}`
    : `${indicator} ${operatorLabel(operator).toLowerCase()} ${threshold}`;
}

function operatorLabel(value) {
  return OPERATORS.find(([candidate]) => candidate === value)?.[1] || value;
}

function alertConditionLabel(alert) {
  if (!alert.indicatorId) {
    return `${operatorLabel(alert.operator)} ${formatPrice(
      alert.thresholdPrice,
      alert.pricePrecision
    )}`;
  }
  const indicator = indicatorSummary(alert);
  if (alert.type === "IndicatorPair") {
    return `${indicator} ${operatorLabel(alert.operator).toLowerCase()} ${
      indicatorBSummary(alert)
    }`;
  }
  return alert.type === "PriceIndicator"
    ? `Price ${operatorLabel(alert.operator).toLowerCase()} ${indicator}`
    : `${indicator} ${operatorLabel(alert.operator).toLowerCase()} ${formatIndicatorNumber(
        alert.thresholdPrice
      )}`;
}

function compactConditionText(value) {
  let condition = String(value || "Alert condition")
    .replace(/\bCrossesAbove\b/gi, "crossing above")
    .replace(/\bCrossesBelow\b/gi, "crossing below")
    .replace(/\bCrossesEither\b/gi, "crossing either direction")
    .replace(/\bcrosses above\b/gi, "crossing above")
    .replace(/\bcrosses below\b/gi, "crossing below")
    .replace(/\bcrosses either direction\b/gi, "crossing either direction")
    .replace(/^Price\s+/i, "")
    .replace(
      /\s+\(([-+]?\d+(?:\.\d+)?(?:\s*,\s*[-+]?\d+(?:\.\d+)?)*)\)/g,
      (_, parameters) => {
        const compact = parameters.replace(/\s+/g, "");
        return compact.includes(",") ? ` (${compact})` : compact;
      }
    )
    .trim();
  condition = condition.charAt(0).toUpperCase() + condition.slice(1);
  return condition;
}

function indicatorSummary(alert) {
  const parameters = Object.entries(parseParameters(alert.indicatorParametersJson))
    .filter(([key, value]) => key !== "source" && value !== "0" && value !== "none")
    .map(([, value]) => value)
    .join(", ");
  const name = alert.indicatorOutputDisplayName || alert.indicatorId?.toUpperCase() || "Indicator";
  return parameters ? `${name} (${parameters})` : name;
}

function indicatorBSummary(alert) {
  const parameters = Object.entries(parseParameters(alert.indicatorBParametersJson))
    .filter(([key, value]) => key !== "source" && value !== "0" && value !== "none")
    .map(([, value]) => value)
    .join(", ");
  const name = alert.indicatorBOutputDisplayName
    || alert.indicatorBId?.toUpperCase()
    || "Indicator B";
  return parameters ? `${name} (${parameters})` : name;
}

function unlinkIndicator(indicator) {
  if (!indicator) return null;
  return {
    ...indicator,
    sourceIndicatorId: null,
    sourceIndicatorVersion: null
  };
}

function indicatorOutputsCompatible(outputA, outputB) {
  if (!outputA || !outputB) return false;
  const domainsA = outputA.allowedComparisonDomains || [];
  const domainsB = outputB.allowedComparisonDomains || [];
  return outputA.comparisonUnitKey === outputB.comparisonUnitKey
    && domainsA.includes(outputB.comparisonDomain)
    && domainsB.includes(outputA.comparisonDomain);
}

function parseParameters(value) {
  if (!value) return {};
  if (typeof value === "object") return { ...value };
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function replaceSelectOptions(select, options) {
  select.replaceChildren(...options.map(([value, label, disabled = false]) => {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    option.disabled = disabled;
    return option;
  }));
  refreshChartDropdown(select);
}

function editorIndicatorFromCapability(capability) {
  const output = capability.outputs.find((item) => item.supportsFixedValueComparison);
  return {
    instance: null,
    definition: {
      id: capability.id,
      name: capability.displayName,
      abbreviation: capability.displayName
    },
    capability,
    resourceId: "",
    resourceVersion: null,
    parameters: Object.fromEntries(
      capability.parameters.map((parameter) => [
        parameter.id,
        String(parameter.defaultValue)
      ])
    ),
    inputSeries: capability.supportedInputSeries[0],
    outputSeries: output?.id || capability.outputs[0]?.id,
    adjustmentMode: "Unadjusted"
  };
}

function editorIndicatorFromChart(indicator, definition, capability, versions) {
  const output = capability.outputs.find((candidate) =>
    candidate.supportsFixedValueComparison &&
    definition.outputs.some((definitionOutput) =>
      definitionOutput.key === candidate.id &&
      !["fill", "level"].includes(definitionOutput.kind)
    )
  ) || capability.outputs.find((candidate) => candidate.supportsFixedValueComparison);
  return {
    instance: indicator,
    definition,
    capability,
    resourceId: indicator.id,
    resourceVersion: versions.get(indicator.id) ?? null,
    parameters: Object.fromEntries(
      capability.parameters.map((parameter) => [
        parameter.id,
        String(indicator.inputs?.[parameter.id] ?? parameter.defaultValue)
      ])
    ),
    inputSeries: String(
      indicator.inputs?.source || capability.supportedInputSeries[0]
    ),
    outputSeries: output?.id || capability.outputs[0]?.id,
    adjustmentMode: "Unadjusted"
  };
}

function cloneEditorIndicatorContext(context) {
  return {
    ...context,
    parameters: { ...(context.parameters || {}) }
  };
}

function chartIndicatorLabel(indicator, definition) {
  if (!definition) return indicator.displayName || indicator.definitionId;
  const summary = (definition.summaryInputs || [])
    .filter((key) => key !== "source")
    .map((key) => indicator.inputs?.[key])
    .filter((value) => value !== undefined && value !== null && value !== "")
    .join(", ");
  return summary
    ? `${definition.abbreviation} (${summary})`
    : definition.abbreviation;
}

function sameIndicatorParameters(left = {}, right = {}) {
  const leftEntries = Object.entries(left).sort(([a], [b]) => a.localeCompare(b));
  const rightEntries = Object.entries(right).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(leftEntries) === JSON.stringify(rightEntries);
}

function isIndicatorChoice(value) {
  return String(value || "").startsWith("indicator:")
    || String(value || "").startsWith("snapshot:");
}

function lastIndicatorValue(instance, outputId) {
  const values = instance?.outputs?.[outputId];
  if (!values?.length) return NaN;
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = Number(values[index]);
    if (Number.isFinite(value)) return value;
  }
  return NaN;
}

function formatIndicatorValue(value, capability, outputId) {
  const output = capability.outputs.find((item) => item.id === outputId);
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: Math.min(10, Number(output?.displayPrecision ?? 6))
  });
}

function formatIndicatorNumber(value) {
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString(undefined, { maximumFractionDigits: 10 })
    : "\u2014";
}

function timingLabel(alert) {
  return alert.triggerTiming === "CompletedBar"
    ? `Completed ${alert.timeframe || "bar"}`
    : "Immediate";
}

function frequencyLabel(value) {
  return {
    Once: "Once",
    OncePerBar: "Once per bar",
    EveryOccurrence: "Every occurrence"
  }[value] || value;
}

function hasChannel(value, channel) {
  if (Array.isArray(value)) return value.includes(channel);
  return String(value || "").split(/,\s*/).includes(channel);
}

function channelLabel(value) {
  return String(value || "").replaceAll(",", " +") || "\u2014";
}

function formatPrice(value, precision = 2, currency = true) {
  const price = Number(value);
  if (!Number.isFinite(price)) return "\u2014";
  const formatted = price.toLocaleString(undefined, {
    minimumFractionDigits: Math.min(precision, 4),
    maximumFractionDigits: Math.min(precision, 8)
  });
  return currency ? `$${formatted}` : formatted;
}

function formatTime(value) {
  if (!value) return "\u2014";
  const date = new Date(value);
  return Number.isFinite(date.valueOf())
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short"
      }).format(date)
    : "\u2014";
}

function toLocalDateTimeValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.valueOf())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function lastPrice(instance) {
  const closes = instance?.data?.closes;
  return closes?.length ? Number(closes[closes.length - 1]) : NaN;
}

function lastSeriesValue(values) {
  if (!values?.length) return null;
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = Number(values[index]);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function suppliedIndicatorValue(
  supplied,
  alertId,
  suffix,
  sourceId,
  indicatorId,
  outputSeries
) {
  const direct = supplied?.[alertId];
  const directValue = suffix ? direct?.indicatorBValue : direct?.indicatorValue;
  if (Number.isFinite(Number(directValue))) return Number(directValue);
  const values = Array.isArray(supplied)
    ? supplied
    : Array.isArray(supplied?.values)
      ? supplied.values
      : [];
  const match = values.find((candidate) =>
    (!sourceId || candidate.sourceIndicatorId === sourceId)
    && candidate.indicatorId === indicatorId
    && candidate.outputSeries === outputSeries
  );
  const value = Number(match?.value);
  return Number.isFinite(value) ? value : null;
}

function sameIdentifier(left, right) {
  return String(left || "").toLowerCase() === String(right || "").toLowerCase();
}

function finiteTimestamp(value) {
  if (value === null || value === undefined || value === "") return null;
  const timestamp = typeof value === "number" ? value : Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function finitePrice(value) {
  const price = Number(value);
  return Number.isFinite(price) && price > 0 ? price : null;
}

function snapPrice(price, tickSize = 0.01, precision = 2) {
  const tick = Number(tickSize) > 0 ? Number(tickSize) : 0.01;
  const snapped = Math.round(Number(price) / tick) * tick;
  return Number(snapped.toFixed(Math.min(10, Number(precision) || 2)));
}

function localPoint(element, event) {
  const rect = element.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
}

function alertGeometry(instance, alert) {
  const info = instance?.workspace?.renderInfo;
  if (!info?.layout) return null;
  if (!["IndicatorValue", "PriceIndicator"].includes(alert.type)) {
    const y = info.priceToY(Number(alert.thresholdPrice));
    return {
      y,
      top: info.layout.priceTop,
      bottom: info.layout.priceBottom
    };
  }
  const indicator = instance.indicatorInstances?.find(
    (item) => item.id === alert.sourceIndicatorId
  );
  if (!indicator) return null;
  const value = alert.type === "IndicatorValue"
    ? Number(alert.thresholdPrice)
    : Number(alert.currentIndicatorValue);
  if (!Number.isFinite(value)) return null;
  if (indicator.paneId === "main") {
    return {
      y: info.priceToY(value),
      top: info.layout.priceTop,
      bottom: info.layout.priceBottom
    };
  }
  const pane = info.layout.indicatorPanes?.find(
    (item) => item.id === indicator.paneId
  );
  const range = info.indicatorPaneRanges?.[indicator.paneId];
  if (!pane || !range || range.spread <= 0) return null;
  return {
    y: pane.top + ((range.maximum - value) / range.spread) * pane.height,
    top: pane.top,
    bottom: pane.bottom
  };
}

function indicatorAlertValueAtY(instance, alert, y) {
  const indicator = instance.indicatorInstances?.find(
    (item) => item.id === alert.sourceIndicatorId
  );
  if (!indicator || indicator.paneId === "main") {
    return instance.workspace.renderInfo?.yToPrice?.(y);
  }
  const mapped = indicatorPaneValueAtY(instance.workspace.renderInfo, y);
  return mapped?.paneId === indicator.paneId ? mapped.value : NaN;
}

function rootChartId(root) {
  const value = root.dataset.chartId;
  return /^[0-9a-f-]{36}$/i.test(value || "") ? value : "";
}

function resizeHandles() {
  return ["n", "e", "s", "w", "ne", "nw", "se", "sw"]
    .map((edge) =>
      `<span class="chart-resize-handle chart-resize-${edge}" data-window-resize="${edge}" aria-hidden="true"></span>`)
    .join("");
}

function trapFocus(element) {
  const focusable = [...element.querySelectorAll(
    "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])"
  )].filter((item) => !item.hidden);
  if (focusable.length === 0) return;
  const handler = (event) => {
    if (event.key === "Escape") {
      element.hidden = true;
      element.removeEventListener("keydown", handler);
      return;
    }
    if (event.key !== "Tab") return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  element.addEventListener("keydown", handler, { once: false });
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function normalizeEndpoint(value) {
  return typeof value === "string" && value.trim()
    ? value.trim().replace(/\/$/, "")
    : null;
}

function normalizedProblemCode(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll("_", "-")
    .toLowerCase();
}

function alertPolicyMessage(code, fallback) {
  const normalized = normalizedProblemCode(code);
  if (normalized.includes("quota") && normalized.includes("alert")) {
    return "You have reached the active alert limit for this product.";
  }
  if (normalized === "quota-exceeded") {
    return "You have reached the active alert limit for this product.";
  }
  if (normalized === "capability-disabled") {
    return "This product does not include alerts.";
  }
  if (normalized === "resource-product-mismatch") {
    return "This alert belongs to another product.";
  }
  if (normalized === "enrollment-expired") {
    return "Your product access has expired.";
  }
  if (normalized === "product-inactive") {
    return "This product is not currently available.";
  }
  if (normalized === "product-access-denied" || normalized === "access-invalid") {
    return "You no longer have access to alerts for this product.";
  }
  return String(fallback || "The alert request failed.");
}

function alertErrorMessage(error) {
  return alertPolicyMessage(error?.code, error?.message);
}

function isProductPolicyError(error) {
  const code = normalizedProblemCode(error?.code);
  return code.includes("quota")
    || [
      "capability-disabled",
      "resource-product-mismatch",
      "enrollment-expired",
      "product-inactive",
      "product-access-denied",
      "access-invalid"
    ].includes(code);
}

function quotaEligibleAlertIds(alerts, maximumActiveAlerts) {
  const active = (Array.isArray(alerts) ? alerts : [])
    .filter((alert) => alert?.status === "Active")
    .sort((left, right) => {
      const leftActivation = Date.parse(left.activatedAtUtc || left.createdAtUtc || 0) || 0;
      const rightActivation = Date.parse(right.activatedAtUtc || right.createdAtUtc || 0) || 0;
      return leftActivation - rightActivation
        || String(left.id || "").localeCompare(String(right.id || ""));
    });
  const limit = Number.isFinite(maximumActiveAlerts)
    ? Math.max(0, Math.trunc(maximumActiveAlerts))
    : active.length;
  return new Set(active.slice(0, limit).map((alert) => alert.id));
}

function alertActionAdditionalSlots(alert, action) {
  if (action === "duplicate") return 1;
  if (["resume", "rearm"].includes(action)) {
    return alert?.status === "Active" ? 0 : 1;
  }
  return 0;
}

export const alertWorkspaceTestables = {
  alertActionAdditionalSlots,
  alertPolicyMessage,
  compactConditionText,
  generatedName,
  quotaEligibleAlertIds,
  rememberTriggerForToast,
  snapPrice,
  updatePayload,
  validateEditorPayload
};
